/**
 * CallDetail (Screen 3) — listen, self-score, then surface AI results.
 *
 * 3-COLUMN call-review workspace (matches the Dashboard aesthetic):
 *   LEFT   — Call Summary · Audio Player · Transcript (synced)
 *   MIDDLE — User Notes · Manager Notes  (role-aware: only the viewer's own
 *            role gets a composer; the other card is read-only)
 *   RIGHT  — Score yourself first (self-score, gated on listen coverage) ·
 *            AI Review & Coaching (locked behind the self-score gate for staff;
 *            carries the Trigger-AI-Review button + processing states)
 *
 * The product-critical behaviors are PRESERVED VERBATIM from the prior version:
 *   • the self-score reveal GATE (a staff owner can't see the AI review/score,
 *     the manager override, or manager notes for their OWN call until they
 *     SUBMIT their self-score — managers/admins are never gated);
 *   • LISTEN COVERAGE tracking (distinct whole-seconds heard / clip duration);
 *     the self-score Submit button stays locked until ≥90% is heard;
 *   • on-demand audio playback, "add note at current time" anchoring, the synced
 *     transcript (seek + active-line highlight + auto-scroll), the rubric form,
 *     the AI reveal, the manager override/flag, and all role-awareness via the
 *     SimulationProvider effectiveRole (admin "act as" still works).
 *
 * Two new things on top of that:
 *   (A) role-aware notes — never two composers at once (see above);
 *   (B) a "Trigger AI Review" button on the AI card when a call has no AI review
 *       yet — it calls setReviewFlag(true) to QUEUE transcription + scoring (the
 *       only mechanism that enqueues the pipeline). For staff the work still
 *       queues but the RESULT stays gated behind their self-score.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiRequestError } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { useAuth } from "@/auth/AuthProvider";
import { useSimulation } from "@/auth/SimulationProvider";
import { LoadingState } from "@/components/LoadingState";
import { ErrorState } from "@/components/ErrorState";
import { FlagStatusBadge } from "@/components/FlagLifecycle";
import { Card, Badge, Button } from "@/components/ui";
import {
  centsToDollars,
  formatDateTime,
  msToClock,
  scoreBand,
  scoreBandLabel,
  secToClock,
} from "@/lib/format";
import {
  IconAlertTriangle,
  IconArrowDownLeft,
  IconArrowUpRight,
  IconBrain,
  IconBulb,
  IconCalendar,
  IconCheckCircle,
  IconChevronDown,
  IconChevronLeft,
  IconClock,
  IconInfo,
  IconNote,
  IconPause,
  IconPhone,
  IconPlay,
  IconSearch,
  IconSkipBack,
  IconSkipForward,
  IconUser,
} from "@/components/icons";
import type {
  AiReveal,
  AppRole,
  AudioSource,
  CallDetail as CallDetailModel,
  CallNote,
  CallType,
  Coaching,
  FlagType,
  PipelineStatus,
  RecordingUrl,
  SelfScore,
  StaffMember,
  TranscriptSource,
} from "@/types";
import { REAL_CALL_TYPES } from "@/types";

/**
 * The role the UI gates on. We prefer the SimulationProvider's effectiveRole
 * (so an admin "acting as" a staff user sees the staff experience), falling
 * back to the real role from AuthProvider. SimulationProvider always wraps the
 * app, so useSimulation is safe here.
 */
function useEffectiveRole(): AppRole | null {
  const { role } = useAuth();
  const { effectiveRole } = useSimulation();
  return effectiveRole ?? role;
}

function isManagerRole(role: AppRole | null): boolean {
  return role === "manager" || role === "admin";
}

/** Bare text color for a 0–100 score (matches the band tokens; mirrors Dashboard). */
function scoreTextClass(total: number): string {
  switch (scoreBand(total)) {
    case "strong":
      return "text-band-strong";
    case "solid":
      return "text-band-solid";
    case "fair":
      return "text-band-fair";
    case "weak":
      return "text-band-weak";
  }
}

// Statuses where a flagged call is still moving through the worker pipeline.
const IN_FLIGHT_STATUSES: PipelineStatus[] = [
  "discovered",
  "recording_ready",
  "transcribing",
  "transcribed",
  "scoring",
];

function isInFlight(status: PipelineStatus): boolean {
  return IN_FLIGHT_STATUSES.includes(status);
}

/** Shared section-card header: an accent icon chip + title + optional aside. */
function SectionHeader({
  icon,
  tone = "primary",
  title,
  info,
  aside,
}: {
  icon: ReactNode;
  tone?: "primary" | "danger" | "gold" | "success";
  title: string;
  /** Small (i) tooltip text shown next to the title. */
  info?: string;
  aside?: ReactNode;
}) {
  const chip = {
    primary: "bg-primary/10 text-primary",
    danger: "bg-band-weak/15 text-band-weak",
    gold: "bg-band-fair/15 text-band-fair",
    success: "bg-band-solid/15 text-band-solid",
  }[tone];
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-2">
        <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${chip}`}>
          {icon}
        </span>
        <h2 className="text-sm font-semibold">{title}</h2>
        {info && (
          <span
            className="inline-flex text-muted-foreground"
            title={info}
            aria-label={info}
          >
            <IconInfo className="h-3.5 w-3.5" />
          </span>
        )}
      </div>
      {aside}
    </div>
  );
}

// ── Call-type framework: staff/type header (§8) ──────────────────────────────
const CALL_TYPE_META: Record<
  CallType,
  { label: string; short: string; tone: "sales" | "admin" | "unknown" }
> = {
  automotive_tint: { label: "Automotive Tint", short: "TINT", tone: "sales" },
  ppf: { label: "PPF / Ceramic", short: "PPF", tone: "sales" },
  arch_residential: { label: "Architectural — Residential", short: "HOME", tone: "sales" },
  arch_commercial: { label: "Architectural — Commercial", short: "COMM", tone: "sales" },
  non_sales_admin: { label: "Non-Sales / Admin", short: "SVC", tone: "admin" },
  unknown: { label: "Detecting call type…", short: "—", tone: "unknown" },
};

/** Badge label + tone for a call type (used by the staff/type header). */
export function getCallTypeBadgeProps(callType: CallType | null) {
  return CALL_TYPE_META[callType ?? "unknown"] ?? CALL_TYPE_META.unknown;
}

const OUTCOME_LABELS: Record<string, string> = {
  booked: "Booked",
  consult_booked: "Consult booked",
  quote_given_no_commit: "Quote — no commit",
  no_next_step: "No next step",
  info_only: "Info only",
  excluded: "Excluded",
};

const CONF_LABEL: Record<string, string> = { high: "High", medium: "Medium", low: "Low" };

/**
 * Transcript-provenance badge (Phase 4 cutover). Tells the reviewer WHICH
 * transcript the AI score was produced from:
 *   • 'live' (supervised, rep/customer roles KNOWN) → a confident "Scored from
 *     live transcript" badge. This is the higher-trust source.
 *   • 'recording' (RingCentral recording + batch STT, roles DIARIZED/guessed) →
 *     a muted "Scored from recording" badge.
 * Reads only the ungated `transcript_source` field, so it renders before a rep
 * self-scores. Renders nothing until a transcript is resolved (source === null).
 */
const TRANSCRIPT_SOURCE_LABEL: Record<TranscriptSource, string> = {
  live: "Scored from live transcript",
  recording: "Scored from recording",
};

function TranscriptSourceBadge({ call }: { call: CallDetailModel }) {
  const source = call.transcript_source;
  if (!source) return null;
  const isLive = source === "live";
  const reason = call.transcript_source_reason?.trim() || undefined;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ${
        isLive
          ? "bg-band-solid/15 text-band-solid"
          : "bg-muted text-muted-foreground"
      }`}
      title={
        reason ??
        (isLive
          ? "Roles are known from per-leg live supervision — higher-trust scoring."
          : "Roles were inferred by diarization from the RingCentral recording.")
      }
    >
      {isLive ? (
        <IconBrain className="h-3.5 w-3.5" aria-hidden />
      ) : (
        <IconPhone className="h-3.5 w-3.5" aria-hidden />
      )}
      {TRANSCRIPT_SOURCE_LABEL[source]}
    </span>
  );
}

/** Manager-only override panel: re-classify, re-attribute, exclude/un-exclude. */
function CallTypeManagerOverride({ call }: { call: CallDetailModel }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.calls.detail(call.id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.calls.all });
  };
  const staffQuery = useQuery({
    queryKey: queryKeys.manager.staff,
    queryFn: ({ signal }) => api.getManagerStaff(signal),
    enabled: open,
  });
  const typeMut = useMutation({
    mutationFn: (ct: CallType) => api.overrideCallType(call.id, ct),
    onSuccess: invalidate,
  });
  const repMut = useMutation({
    mutationFn: (repId: string | null) => api.overrideRep(call.id, repId),
    onSuccess: invalidate,
  });
  const exclMut = useMutation({
    mutationFn: (excluded: boolean) => api.toggleExclude(call.id, excluded),
    onSuccess: invalidate,
  });
  const busy = typeMut.isPending || repMut.isPending || exclMut.isPending;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs font-medium text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
      >
        Manager override…
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-2 rounded-md border bg-muted/30 p-2.5 text-xs">
      <div className="flex items-center gap-2">
        <label className="w-20 shrink-0 text-muted-foreground">Call type</label>
        <select
          className="flex-1 rounded border bg-background px-2 py-1"
          defaultValue={call.call_type ?? ""}
          disabled={busy}
          onChange={(e) => {
            const v = e.target.value as CallType;
            if (v && v !== call.call_type) typeMut.mutate(v);
          }}
        >
          <option value="" disabled>
            Set type…
          </option>
          {REAL_CALL_TYPES.map((ct) => (
            <option key={ct} value={ct}>
              {CALL_TYPE_META[ct].label}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <label className="w-20 shrink-0 text-muted-foreground">Rep</label>
        <select
          className="flex-1 rounded border bg-background px-2 py-1"
          defaultValue={call.rep_override_id ?? ""}
          disabled={busy || !staffQuery.data}
          onChange={(e) => repMut.mutate(e.target.value || null)}
        >
          <option value="">Auto (detected / line owner)</option>
          {(staffQuery.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
              {s.extension_number ? ` · ext ${s.extension_number}` : ""}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="ghost"
          onClick={() => exclMut.mutate(!call.excluded_from_stats)}
          disabled={busy}
        >
          {call.excluded_from_stats ? "Un-exclude (score it)" : "Exclude from stats"}
        </Button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-muted-foreground hover:text-foreground"
        >
          Close
        </button>
      </div>
      {busy && <p className="text-muted-foreground">Saving… (a re-score may be queued)</p>}
    </div>
  );
}

/**
 * CallTypeStaffHeader (§8) — ALWAYS visible (ungated): the call-type badge, the
 * call-outcome chip, the rep who actually handled it (with an extension-owner
 * mismatch flag), classifier confidence, the excluded/flagged states, and a
 * manager-only override. Reads only ungated CallDetail fields, so it renders
 * before a rep self-scores.
 */
function CallTypeStaffHeader({
  call,
  isManager,
}: {
  call: CallDetailModel;
  isManager: boolean;
}) {
  const meta = getCallTypeBadgeProps(call.call_type ?? null);
  const toneClass =
    meta.tone === "sales"
      ? "bg-primary/10 text-primary"
      : meta.tone === "admin"
        ? "bg-band-fair/15 text-band-fair"
        : "bg-muted text-muted-foreground";
  const repName = call.evaluated_rep_name ?? call.detected_rep_name ?? null;
  const outcome = call.call_outcome ? OUTCOME_LABELS[call.call_outcome] ?? call.call_outcome : null;

  return (
    <Card className="p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${toneClass}`}
          title={meta.label}
        >
          {meta.short} · {meta.label}
        </span>
        {outcome && (
          <span className="inline-flex items-center rounded-md bg-accent px-2 py-0.5 text-xs font-medium text-foreground">
            {outcome}
          </span>
        )}
        {call.call_type_confidence && (
          <span className="text-xs text-muted-foreground">
            confidence: {CONF_LABEL[call.call_type_confidence] ?? call.call_type_confidence}
            {call.call_type_source === "manual" ? " · manager-set" : ""}
          </span>
        )}
        {/* Phase 4: which transcript the score came from (live vs recording). */}
        <TranscriptSourceBadge call={call} />
        <span className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground">
          <IconUser className="h-3.5 w-3.5" />
          {repName ?? "Unknown rep"}
          {call.extension_number ? ` · ext ${call.extension_number}` : ""}
        </span>
      </div>

      {call.staff_name_ext_mismatch && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-band-fair/10 px-2 py-1 text-xs text-band-fair">
          <IconAlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Answered by {repName ?? "another rep"}, but ext {call.extension_number} belongs to{" "}
            {call.ext_owner_name ?? "someone else"}.
            {/* Re-attribution is a manager action — don't tell staff to do it. */}
            {isManager ? " Verify attribution." : ""}
          </span>
        </p>
      )}

      {call.excluded_from_stats && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <IconInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Excluded from stats{call.excluded_reason ? ` — ${call.excluded_reason}` : ""}. Not
            scored; kept out of all averages.
          </span>
        </p>
      )}

      {call.call_type === "non_sales_admin" && !call.excluded_from_stats && (
        <p className="mt-2 text-xs text-muted-foreground">
          Graded on service quality (professionalism, accuracy, clear next step) — not sales.
        </p>
      )}

      {isManager && (
        <div className="mt-2">
          <CallTypeManagerOverride call={call} />
        </div>
      )}
    </Card>
  );
}

export function CallDetail() {
  const { id = "" } = useParams<{ id: string }>();

  const callQuery = useQuery({
    queryKey: queryKeys.calls.detail(id),
    queryFn: ({ signal }) => api.getCall(id, signal),
    enabled: !!id,
    // Poll while a flagged call is still being processed; stop once it lands on
    // a terminal status (scored / error / skipped) or was never flagged.
    refetchInterval: (query) => {
      const call = query.state.data;
      if (!call) return false;
      return call.review_requested && isInFlight(call.status) ? 5000 : false;
    },
  });

  if (callQuery.isLoading) {
    return <LoadingState label="Loading call…" />;
  }
  if (callQuery.isError || !callQuery.data) {
    return (
      <ErrorState
        error={callQuery.error}
        onRetry={() => void callQuery.refetch()}
      />
    );
  }

  return <CallDetailLoaded call={callQuery.data} />;
}

/** Inner component: data is loaded, so hooks can depend on the call freely. */
function CallDetailLoaded({ call }: { call: CallDetailModel }) {
  const { user } = useAuth();
  const { simulatedUser } = useSimulation();
  const effectiveRole = useEffectiveRole();
  // The identity the call is evaluated against: the simulated user while an admin
  // is "viewing as" a rep, otherwise the signed-in user. Keeps the self-score
  // owner check consistent with what the server does via the X-Act-As header.
  const effectiveUserId = simulatedUser?.id ?? user?.id ?? null;
  const isManager = isManagerRole(effectiveRole);

  // Shared audio element: the player lives in AudioSection, but AI evidence
  // chips + note timestamps need to seek + play it, so we own the ref here and
  // pass a seek helper.
  const audioRef = useRef<HTMLAudioElement>(null);

  // ── Live-transcript playback offset (Phase 4 cutover) ─────────────────────
  // Transcript segments / evidence / note timestamps are in the SCORED clock.
  // When the call was scored from the LIVE transcript but we're playing the
  // RingCentral recording, the live leg connected `offset_ms` AFTER the recording
  // started, so the recording position = transcript_ms + offset (and the live
  // position from the recording = audio_ms − offset). When the audio IS the live
  // dual-channel WAV, the clocks already share t0 → offset 0. Reported up by
  // AudioSection once the recording-url response (which carries it) loads;
  // best-effort: defaults to 0 so existing recording-sourced playback is unchanged.
  const [audioOffsetMs, setAudioOffsetMs] = useState(0);

  const seekTo = (ms: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    // ms is a transcript/scored-clock position → shift into the audio clock.
    audio.currentTime = Math.max(0, (ms + audioOffsetMs) / 1000);
    void audio.play().catch(() => {
      /* autoplay can be blocked; the user can press play manually */
    });
  };

  // Live audio position (ms), updated on the <audio> timeupdate event. Drives the
  // synced transcript highlight + auto-scroll AND the "add note at current time"
  // anchor.
  const [currentMs, setCurrentMs] = useState(0);
  // The audio position re-expressed in the transcript/scored clock (audio − offset,
  // floored at 0). The synced transcript + note composer read THIS so highlighting
  // and note anchoring line up with the scored transcript regardless of source.
  const transcriptMs = Math.max(0, currentMs - audioOffsetMs);

  // ── Listen coverage (drives the self-score gate) ──────────────────────────
  // We track every whole-second mark the rep has actually heard (added on each
  // timeupdate) so skipping ahead doesn't count as "listened". coverage =
  // distinct seconds heard / clip duration. Duration comes from the <audio>
  // metadata. We keep the Set in a ref (no re-render per tick) and bump a small
  // `coveragePct` state only when the integer percentage actually changes.
  const heardSecondsRef = useRef<Set<number>>(new Set());
  const [durationSec, setDurationSec] = useState(0);
  const [coveragePct, setCoveragePct] = useState(0);

  const recomputeCoverage = (durSec: number) => {
    const denom = Math.max(1, Math.floor(durSec));
    const pct = Math.min(
      100,
      Math.round((heardSecondsRef.current.size / denom) * 100),
    );
    setCoveragePct((prev) => (prev === pct ? prev : pct));
  };

  const handleTimeUpdate = (ms: number) => {
    setCurrentMs(ms);
    if (ms > 0) {
      const sec = Math.floor(ms / 1000);
      if (!heardSecondsRef.current.has(sec)) {
        heardSecondsRef.current.add(sec);
        recomputeCoverage(durationSec);
      }
    }
  };

  const handleDuration = (durSec: number) => {
    if (!Number.isFinite(durSec) || durSec <= 0) return;
    setDurationSec(durSec);
    recomputeCoverage(durSec);
  };

  // The rep owns this call AND is acting as staff → gated self-score loop.
  // Ownership keys on evaluated_rep_id (§7.11: the rep who actually handled the
  // call, not just the line owner), falling back to ext_owner when unresolved.
  // Managers/admins (real or simulated) keep seeing AI + coaching directly.
  const ownerId = call.evaluated_rep_id ?? call.ext_owner;
  const isOwner =
    !!effectiveUserId && ownerId === effectiveUserId && effectiveRole === "staff";
  // A manager/admin can themselves be the evaluated rep on a call they handled
  // (isOwner is false — they're not gated). Their "rep self-score" IS their own,
  // so viewer-facing copy should stay second person and the manager recap card
  // shouldn't nag them about a form managers never get.
  const viewerIsEvaluatedRep =
    !!effectiveUserId && call.evaluated_rep_id === effectiveUserId;
  // Staff viewer on a call that is NOT theirs to review: covered (someone else
  // handled it on their line) or unattributed (no evaluated rep at all — e.g. a
  // call on a mapped extension whose ext_owner snapshot is null). Either way
  // nothing evaluative is theirs — no AI review trigger, no manager notes, no
  // self-score. (For staff, the server enforces the same rule with 403s.)
  const coveredViewer =
    effectiveRole === "staff" &&
    !(call.evaluated_rep_id != null && call.evaluated_rep_id === effectiveUserId);

  // The single gate boolean used across notes / AI / coaching: a staff owner who
  // hasn't submitted their self-score yet is gated; everyone else is not.
  const gated = isOwner && !call.self_score_submitted;

  // ── Trigger AI Review (header action) ─────────────────────────────────────
  // The ONLY enqueue mechanism is the review flag; we mirror it optimistically so
  // the header button reacts instantly, and the detail poll advances the status.
  const queryClient = useQueryClient();
  const [triggerOptimistic, setTriggerOptimistic] = useState<boolean | null>(null);
  const reviewRequested = triggerOptimistic ?? call.review_requested;
  const triggerMutation = useMutation({
    mutationFn: () => api.setReviewFlag(call.id, true),
    onMutate: () => setTriggerOptimistic(true),
    onError: () => setTriggerOptimistic(null),
    onSuccess: (data) => {
      setTriggerOptimistic(data.review_requested);
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls.detail(call.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.calls.all });
    },
  });
  const aiScored = call.ai_reveal?.score != null || call.status === "scored";
  const aiProcessing = reviewRequested && isInFlight(call.status);
  // A covered-call line owner can't trigger (the server 403s — flagging enqueues
  // paid work on someone else's coaching loop), so don't offer the button.
  const canTriggerAi =
    !reviewRequested && !aiScored && call.status !== "error" && !coveredViewer;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Slim ACTION header — back nav + compact context + the primary actions.
          (Call identity/details live in the Call Summary card, not repeated here.) */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b bg-card px-5 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/calls"
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground"
          >
            <IconChevronLeft className="h-4 w-4" /> Back
          </Link>
          <span className="h-5 w-px bg-border" aria-hidden />
          <DirectionPill direction={call.direction} />
          <span className="truncate text-sm text-muted-foreground">
            {formatDateTime(call.started_at)} · Ext {call.extension_number}
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {aiProcessing && (
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Spinner /> AI review {(STATUS_LABELS[call.status] ?? "processing").toLowerCase()}…
            </span>
          )}
          {triggerMutation.isError && !aiProcessing && (
            <span className="text-sm text-destructive">
              Couldn't queue the AI review. Please try again.
            </span>
          )}
          {canTriggerAi && (
            <Button
              variant="primary"
              onClick={() => triggerMutation.mutate()}
              disabled={triggerMutation.isPending}
            >
              {triggerMutation.isPending ? "Queuing…" : "Trigger AI Review"}
            </Button>
          )}
        </div>
      </div>

      {/* SCAN-FIRST dashboard grid that FILLS the viewport. At xl the three
          columns each scroll INTERNALLY so the PAGE SHELL never scrolls; only a
          column (or a card inside it) scrolls when its own content overflows.
          The AI/review column is the widest (1.2fr) so the scoreboard + coaching
          dominate. Below xl it stacks + scrolls normally. */}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5 xl:grid xl:grid-cols-[minmax(320px,0.95fr)_minmax(320px,0.9fr)_minmax(440px,1.2fr)] xl:gap-5 xl:overflow-hidden">
        {/* LEFT — Summary + Audio pinned; Transcript fills + scrolls internally */}
        <div className="flex flex-col gap-4 xl:min-h-0 xl:overflow-hidden">
          <CallInfo call={call} />
          <AudioSection
            call={call}
            audioRef={audioRef}
            currentMs={currentMs}
            durationSec={durationSec}
            onTimeUpdate={handleTimeUpdate}
            onDuration={handleDuration}
            onOffset={setAudioOffsetMs}
          />
          {/* Transcript + notes read the SCORED clock (audio − offset) so a live-
              sourced transcript lines up with RC-recording playback. */}
          <TranscriptSection call={call} onSeek={seekTo} currentMs={transcriptMs} />
        </div>

        {/* MIDDLE — role-aware notes (one composer at most); each card caps its
            own note list, so the column itself rarely needs to scroll. */}
        <div className="flex flex-col gap-4 xl:min-h-0 xl:overflow-y-auto xl:pr-1">
          <UserNotesCard
            call={call}
            currentMs={transcriptMs}
            onSeek={seekTo}
            canCompose={!isManager}
          />
          <ManagerNotesCard
            call={call}
            currentMs={transcriptMs}
            onSeek={seekTo}
            canCompose={isManager}
            gated={gated}
            coveredViewer={coveredViewer}
          />
        </div>

        {/* RIGHT — call-type/staff header (ungated) then self-score, AI review,
            coaching, actions; column scrolls */}
        <div className="flex flex-col gap-4 xl:min-h-0 xl:overflow-y-auto xl:pr-1">
          <CallTypeStaffHeader call={call} isManager={isManager} />
          {isOwner && <SelfScoreSection call={call} coveragePct={coveragePct} />}
          {/* Manager/admin reviewing someone else's call: the rep's submitted
              self-score (per-criterion points + notes) is part of what they're
              coaching on, so show the full read-only recap — not just the total. */}
          {!isOwner && isManager && (
            <RepSelfScoreSection call={call} viewerIsEvaluatedRep={viewerIsEvaluatedRep} />
          )}
          <AiReviewSection
            call={call}
            onSeek={seekTo}
            gated={gated}
            ownerView={isOwner || viewerIsEvaluatedRep}
            canReadSelfScore={isManager || isOwner || viewerIsEvaluatedRep}
            coveredViewer={coveredViewer}
          />
          <CoachingDisplay call={call} />
          <ManagerActions call={call} />
          {/* ADMIN-ONLY (locked decision): per-call processing cost. TOTAL
              ABSENCE for non-admins — no locked placeholder, no fetch. Keys on
              the EFFECTIVE role so an admin "viewing as" a rep sees exactly
              what the rep sees (nothing). */}
          {effectiveRole === "admin" && <AdminCostCard callId={call.id} />}
        </div>
      </div>
    </div>
  );
}

// ── Direction pill (matches the Dashboard) ───────────────────────────────────
function DirectionPill({ direction }: { direction: CallDetailModel["direction"] }) {
  if (direction === "Inbound") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-band-solid/15 px-2 py-0.5 text-xs font-medium text-band-solid">
        <IconArrowDownLeft className="h-3 w-3" /> Inbound
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
      <IconArrowUpRight className="h-3 w-3" /> Outbound
    </span>
  );
}

// ── Call summary (compact metadata) ──────────────────────────────────────────
/** Strip a phone number to its digits so a "name" that is really the number can
 *  be detected even when formatted differently ("(317) 555-1212" vs "3175551212"). */
function digitsOnly(s: string | null | undefined): string {
  return (s ?? "").replace(/\D/g, "");
}

/**
 * Build the compact view model. RingCentral sometimes returns the caller's
 * NAME as the phone number itself (or a duplicate of the external number); in
 * that case we show the number ONCE and leave the name blank rather than
 * repeating it. Long names are clamped by the renderer (truncate + title).
 */
function callInfoView(call: CallDetailModel): {
  displayCustomerName: string | null;
  displayExternalNumber: string;
  displayExtension: string;
  displayDuration: string;
  displayResult: string | null;
} {
  const counterpartNumber =
    call.direction === "Inbound" ? call.from_number : call.to_number;
  const counterpartName =
    call.direction === "Inbound" ? call.from_name : call.to_name;

  const externalNumber = counterpartNumber?.trim() || "";
  const rawName = counterpartName?.trim() || "";

  // Treat the "name" as absent when it's empty, equals the external number, or
  // is just the same string of digits as the number.
  const nameDigits = digitsOnly(rawName);
  const numberDigits = digitsOnly(externalNumber);
  const nameIsJustNumber =
    rawName.length > 0 &&
    ((numberDigits.length > 0 && nameDigits === numberDigits) ||
      rawName === externalNumber);
  const displayCustomerName = rawName.length > 0 && !nameIsJustNumber ? rawName : null;

  return {
    displayCustomerName,
    displayExternalNumber: externalNumber || "—",
    displayExtension: call.extension_number || "—",
    displayDuration: secToClock(call.duration_sec),
    displayResult: call.call_result?.trim() || null,
  };
}

function CallInfo({ call }: { call: CallDetailModel }) {
  const view = callInfoView(call);

  // Compact metadata rows: each is a small label + value, read as a dense strip
  // rather than a primary content panel.
  const rows: Array<{ icon: ReactNode; label: string; value: ReactNode; clamp?: boolean }> = [
    {
      icon: <IconUser className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "Customer",
      value: view.displayCustomerName ?? (
        <span className="text-muted-foreground">Unknown</span>
      ),
      clamp: true,
    },
    {
      icon: <IconPhone className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "Number",
      value: <span className="tabular-nums">{view.displayExternalNumber}</span>,
    },
    {
      icon: <IconCalendar className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "When",
      value: formatDateTime(call.started_at),
      clamp: true,
    },
    {
      icon: <IconClock className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "Duration",
      value: <span className="tabular-nums">{view.displayDuration}</span>,
    },
    {
      icon: <IconUser className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "Ext.",
      value: <span className="tabular-nums">{view.displayExtension}</span>,
    },
    {
      icon: <IconPhone className="h-3.5 w-3.5 text-muted-foreground" />,
      label: "Result",
      value: view.displayResult ?? <span className="text-muted-foreground">—</span>,
      clamp: true,
    },
  ];

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconPhone className="h-3.5 w-3.5" />}
        title="Call Summary"
        info="Details RingCentral reported for this call."
        aside={<DirectionPill direction={call.direction} />}
      />
      <dl className="grid grid-cols-2 gap-x-5 gap-y-2">
        {rows.map((r) => (
          <div key={r.label} className="flex min-w-0 items-baseline gap-2">
            <dt className="flex shrink-0 items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {r.icon}
              {r.label}
            </dt>
            <dd
              className={`min-w-0 text-sm ${r.clamp ? "truncate" : ""}`}
              title={r.clamp && typeof r.value === "string" ? r.value : undefined}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// ── Audio player (on demand) ─────────────────────────────────────────────────
/**
 * On-demand recording load + native HTML5 playback, wrapped in a styled control
 * strip toward the mockup's waveform look. A true waveform needs offline audio
 * analysis; we render a lightweight progress "bar" with a playhead instead and
 * keep the real <audio> element (hidden) as the source of truth so PLAYBACK,
 * COVERAGE tracking (onTimeUpdate / onDuration), and seek-to-time all keep
 * working untouched.
 */
const SKIP_SEC = 15;
const SPEEDS = [1, 1.25, 1.5, 2] as const;

function AudioSection({
  call,
  audioRef,
  currentMs,
  durationSec,
  onTimeUpdate,
  onDuration,
  onOffset,
}: {
  call: CallDetailModel;
  audioRef: RefObject<HTMLAudioElement>;
  currentMs: number;
  durationSec: number;
  onTimeUpdate: (ms: number) => void;
  // Reports the clip length (seconds) once the browser has it, so the parent
  // can compute listen coverage for the self-score gate.
  onDuration: (durationSec: number) => void;
  // Reports the recording-t0 → live-t0 offset (ms) once the recording-url
  // response loads, so the parent aligns a LIVE transcript to RC-recording
  // playback (Phase 4 cutover). 0 when the audio shares the transcript's clock.
  onOffset: (offsetMs: number) => void;
}) {
  const hasRecording = call.recording_rc_id !== null;

  // Lazy: only fires when the user clicks "Load audio" (mutation, not auto-query),
  // so we never download the recording until someone wants to listen.
  const recordingMutation = useMutation<RecordingUrl, unknown, void>({
    mutationFn: () => api.getRecordingUrl(call.id),
    onSuccess: (data) => {
      // Phase 4: a live dual-channel WAV shares the transcript clock (offset 0);
      // the RC recording needs the stored offset to sync a live transcript.
      const offset =
        data.audio_source === "live_dual_wav" ? 0 : data.offset_ms ?? 0;
      onOffset(Number.isFinite(offset) ? Math.max(0, offset) : 0);
    },
  });

  // Phase 4: how to describe the loaded audio source (best-effort; the recording-
  // url response may not carry it, in which case we fall back to the call row).
  const audioSource: AudioSource | null =
    recordingMutation.data?.audio_source ?? call.audio_source ?? null;
  const isDualWav =
    recordingMutation.data?.dual_channel === true ||
    audioSource === "live_dual_wav";

  const [playing, setPlaying] = useState(false);
  const [speedIdx, setSpeedIdx] = useState(0);
  const [volume, setVolume] = useState(1);

  const isRateLimited =
    recordingMutation.error instanceof ApiRequestError &&
    recordingMutation.error.status === 503;
  const isNotFound =
    recordingMutation.error instanceof ApiRequestError &&
    recordingMutation.error.status === 404;

  const loaded = !!recordingMutation.data;
  const totalSec = durationSec || call.duration_sec || 0;
  const playedPct =
    totalSec > 0 ? Math.min(100, (currentMs / 1000 / totalSec) * 100) : 0;

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => {});
    else audio.pause();
  };
  const skip = (delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(totalSec || audio.duration, audio.currentTime + delta));
  };
  const cycleSpeed = () => {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    const audio = audioRef.current;
    if (audio) audio.playbackRate = SPEEDS[next] ?? 1;
  };
  const onScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || totalSec <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * totalSec;
  };

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconPlay className="h-3.5 w-3.5" />}
        title="Audio Player"
        aside={
          hasRecording && !loaded ? (
            <Button
              variant="primary"
              onClick={() => recordingMutation.mutate()}
              disabled={recordingMutation.isPending}
            >
              {recordingMutation.isPending ? "Loading audio…" : "Load audio"}
            </Button>
          ) : undefined
        }
      />

      {!hasRecording && (
        <p className="text-sm text-muted-foreground">No recording for this call.</p>
      )}

      {hasRecording && (
        <>
          {recordingMutation.isPending && (
            <p className="text-sm text-muted-foreground">
              Fetching the recording — this can take a moment the first time.
            </p>
          )}

          {recordingMutation.isError && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-destructive">
                {isNotFound
                  ? "No recording for this call."
                  : isRateLimited
                    ? "RingCentral is busy right now. Please try again in a moment."
                    : "We couldn't load the audio. Please try again."}
              </p>
              {!isNotFound && (
                <div>
                  <Button
                    variant="secondary"
                    onClick={() => recordingMutation.mutate()}
                    disabled={recordingMutation.isPending}
                  >
                    Try again
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* The real <audio>: hidden chrome, but the actual player. Every event
              that drives PLAYBACK + COVERAGE stays wired here unchanged. */}
          {loaded && (
            <audio
              ref={audioRef}
              autoPlay
              preload="auto"
              src={recordingMutation.data!.url}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onTimeUpdate={(e) => onTimeUpdate(e.currentTarget.currentTime * 1000)}
              onLoadedMetadata={(e) => {
                e.currentTarget.playbackRate = SPEEDS[speedIdx] ?? 1;
                e.currentTarget.volume = volume;
                onDuration(e.currentTarget.duration);
              }}
              onDurationChange={(e) => onDuration(e.currentTarget.duration)}
              className="hidden"
            >
              Your browser doesn't support audio playback.
            </audio>
          )}

          {loaded && (
            <div className="flex flex-col gap-3">
              {/* Waveform-style scrubber strip with a playhead. */}
              <div
                onClick={onScrub}
                className="group relative h-12 w-full cursor-pointer overflow-hidden rounded-md bg-primary/5"
                role="slider"
                aria-label="Seek"
                aria-valuenow={Math.round(playedPct)}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {/* faux waveform bars */}
                <div className="pointer-events-none absolute inset-0 flex items-center gap-[3px] px-2">
                  {WAVEFORM_BARS.map((h, i) => {
                    const barPct = (i / WAVEFORM_BARS.length) * 100;
                    const past = barPct <= playedPct;
                    return (
                      <span
                        key={i}
                        style={{ height: `${h}%` }}
                        className={`flex-1 rounded-full ${past ? "bg-primary/70" : "bg-primary/20"}`}
                      />
                    );
                  })}
                </div>
                {/* playhead */}
                <span
                  className="pointer-events-none absolute top-0 bottom-0 w-0.5 bg-primary"
                  style={{ left: `${playedPct}%` }}
                />
              </div>

              {/* Transport row */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => skip(-SKIP_SEC)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border text-muted-foreground hover:bg-accent"
                  title={`Back ${SKIP_SEC}s`}
                  aria-label={`Back ${SKIP_SEC} seconds`}
                >
                  <IconSkipBack className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={togglePlay}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground hover:opacity-90"
                  title={playing ? "Pause" : "Play"}
                  aria-label={playing ? "Pause" : "Play"}
                >
                  {playing ? (
                    <IconPause className="h-5 w-5" />
                  ) : (
                    <IconPlay className="h-5 w-5" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => skip(SKIP_SEC)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full border text-muted-foreground hover:bg-accent"
                  title={`Forward ${SKIP_SEC}s`}
                  aria-label={`Forward ${SKIP_SEC} seconds`}
                >
                  <IconSkipForward className="h-4 w-4" />
                </button>

                <span className="ml-1 font-mono text-xs tabular-nums text-muted-foreground">
                  {msToClock(currentMs)} / {secToClock(totalSec)}
                </span>

                <button
                  type="button"
                  onClick={cycleSpeed}
                  className="ml-auto rounded-md border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent"
                  title="Playback speed"
                >
                  {SPEEDS[speedIdx]}x
                </button>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground" title="Volume">
                  <IconPhone className="h-3.5 w-3.5" aria-hidden />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={volume}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      setVolume(v);
                      if (audioRef.current) audioRef.current.volume = v;
                    }}
                    className="h-1 w-20 accent-primary"
                    aria-label="Volume"
                  />
                </label>
              </div>

              {/* Phase 4: label the audio of record when it's the live stereo WAV
                  (rep-left / customer-right), so the reviewer knows the channels. */}
              {isDualWav && (
                <p className="text-xs text-muted-foreground">
                  Live stereo recording — rep on the left channel, customer on the
                  right.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// Fixed faux-waveform heights (percent of strip), purely decorative.
const WAVEFORM_BARS: number[] = Array.from({ length: 64 }, (_, i) => {
  const v = Math.sin(i * 0.7) * Math.cos(i * 0.27);
  return 25 + Math.abs(v) * 70;
});

// ── Transcript ──────────────────────────────────────────────────────────────
function speakerLabel(speaker: string): string {
  if (speaker === "rep") return "Rep";
  if (speaker === "customer") return "Customer";
  return "Speaker";
}

/** Color-code speakers so the synced transcript is easy to scan. */
function speakerColor(speaker: string): string {
  if (speaker === "rep") return "text-primary";
  if (speaker === "customer") return "text-band-solid";
  return "text-muted-foreground";
}

function TranscriptSection({
  call,
  onSeek,
  currentMs,
}: {
  call: CallDetailModel;
  onSeek: (ms: number) => void;
  currentMs: number;
}) {
  // The transcript only exists once the call reaches `transcribed` (and stays
  // through scoring/scored). Don't fetch before then — it would 404 needlessly.
  const ready =
    call.status === "transcribed" ||
    call.status === "scoring" ||
    call.status === "scored";

  const transcriptQuery = useQuery({
    queryKey: queryKeys.calls.transcript(call.id),
    queryFn: ({ signal }) => api.getTranscript(call.id, signal),
    enabled: ready,
  });

  const segments = transcriptQuery.data ?? [];

  // Synced-transcript controls (display only; never change the data).
  const [search, setSearch] = useState("");
  const [speakerFilter, setSpeakerFilter] = useState<"all" | "rep" | "customer">("all");

  // The segment currently being spoken = the last one whose start_ms <= the audio
  // position. -1 before playback starts (no highlight). Computed over ALL
  // segments (independent of the search/speaker filter) so the highlight is
  // always correct.
  const activeIdx = useMemo(() => {
    if (currentMs <= 0) return -1;
    let idx = -1;
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      if (seg && seg.start_ms <= currentMs) idx = i;
      else break;
    }
    return idx;
  }, [segments, currentMs]);

  const q = search.trim().toLowerCase();
  // We render the full list but visually filter — keeps activeIdx alignment and
  // lets the active line still highlight even when filtered out.
  const visible = useMemo(
    () =>
      segments.map((seg, i) => {
        const matchSpeaker = speakerFilter === "all" || seg.speaker === speakerFilter;
        const matchText = q.length === 0 || seg.text.toLowerCase().includes(q);
        return { seg, i, show: matchSpeaker && matchText };
      }),
    [segments, speakerFilter, q],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLLIElement>(null);

  // Keep the active line centered in the transcript box as the audio plays. Uses
  // bounding rects + scrolls ONLY the container (never the page). Fires on segment
  // change, not on every timeupdate.
  useEffect(() => {
    const container = containerRef.current;
    const active = activeRef.current;
    if (!container || !active) return;
    const cRect = container.getBoundingClientRect();
    const aRect = active.getBoundingClientRect();
    const delta =
      aRect.top - cRect.top - (container.clientHeight / 2 - active.clientHeight / 2);
    container.scrollTo({ top: container.scrollTop + delta, behavior: "smooth" });
  }, [activeIdx]);

  if (!ready) {
    return (
      <Card>
        <SectionHeader
          icon={<IconNote className="h-3.5 w-3.5" />}
          title="Transcript (synced)"
        />
        <p className="text-sm text-muted-foreground">
          The transcript appears once this call has been transcribed.
        </p>
      </Card>
    );
  }

  return (
    <Card className="xl:flex xl:min-h-0 xl:flex-1 xl:flex-col">
      <SectionHeader
        icon={<IconNote className="h-3.5 w-3.5" />}
        title="Transcript (synced)"
      />

      {transcriptQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading transcript…</p>
      )}

      {transcriptQuery.isError && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn't load the transcript. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void transcriptQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      )}

      {transcriptQuery.isSuccess && segments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No transcript text for this call.
        </p>
      )}

      {segments.length > 0 && (
        <>
          {/* search + speaker filter */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="relative flex-1 min-w-[10rem]">
              <IconSearch className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search transcript…"
                className="w-full rounded-md border bg-background py-1.5 pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring"
              />
            </span>
            <select
              value={speakerFilter}
              onChange={(e) =>
                setSpeakerFilter(e.target.value as "all" | "rep" | "customer")
              }
              className="rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring"
              aria-label="Filter by speaker"
            >
              <option value="all">All Speakers</option>
              <option value="rep">Rep</option>
              <option value="customer">Customer</option>
            </select>
          </div>

          <div
            ref={containerRef}
            className="max-h-[24rem] overflow-y-auto pr-1 xl:max-h-none xl:min-h-0 xl:flex-1"
          >
            <ol className="flex flex-col gap-1">
              {visible.map(({ seg, i, show }) => {
                const isActive = i === activeIdx;
                if (!show && !isActive) return null;
                return (
                  <li
                    key={seg.id}
                    ref={isActive ? activeRef : undefined}
                    onClick={() => onSeek(seg.start_ms)}
                    className={`cursor-pointer rounded-md px-2 py-1.5 transition-colors ${
                      isActive
                        ? "bg-band-fair/10 ring-1 ring-band-fair/30"
                        : "hover:bg-accent/50"
                    }`}
                    title="Jump to this moment"
                  >
                    <div className="flex items-center gap-2">
                      {isActive && (
                        <IconPlay className="h-3 w-3 text-band-fair" aria-hidden />
                      )}
                      <span
                        className={`text-xs font-medium uppercase tracking-wide ${speakerColor(seg.speaker)}`}
                      >
                        {speakerLabel(seg.speaker)}
                      </span>
                      <span className="font-mono text-xs text-muted-foreground tabular-nums">
                        {msToClock(seg.start_ms)}
                      </span>
                    </div>
                    <p className={`text-sm ${isActive ? "font-medium" : ""}`}>
                      {seg.text}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>
        </>
      )}
    </Card>
  );
}

// ── Notes (role-aware) ───────────────────────────────────────────────────────
/** ISO timestamp → short relative string ("just now", "5m ago", "3h ago"),
 *  falling back to a short date for anything older than ~a week. */
function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return formatDateTime(iso);
}

/** Shared notes data hook: the list query + the add mutation. Both note cards
 *  read from the SAME endpoints (api.listCallNotes / api.addCallNote) — the
 *  server derives the note SOURCE from the caller's role and gates the GET, so
 *  the cards only differ in which `source` they DISPLAY and who gets a composer. */
function useNotes(callId: string) {
  return useQuery({
    queryKey: queryKeys.calls.notes(callId),
    queryFn: ({ signal }) => api.listCallNotes(callId, signal),
  });
}

/** One note row (timestamp chip → seek, author, relative time, text). */
function NoteRow({
  note,
  onSeek,
}: {
  note: CallNote;
  onSeek: (ms: number) => void;
}) {
  return (
    <li className="flex flex-col gap-1 py-3 first:pt-0">
      <div className="flex flex-wrap items-center gap-2">
        {note.start_ms !== null ? (
          <button
            type="button"
            onClick={() => onSeek(note.start_ms as number)}
            className="inline-flex items-center rounded-full border bg-muted px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title="Jump to this moment"
          >
            {msToClock(note.start_ms)}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">Whole call</span>
        )}
        {note.author_name && (
          <span className="text-xs font-medium text-muted-foreground">
            {note.author_name}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {relativeTime(note.created_at)}
        </span>
      </div>
      <p className="text-sm whitespace-pre-wrap">{note.text}</p>
    </li>
  );
}

/** A timestamp-anchored composer. `accent` colors the submit button: brand navy
 *  for the rep's user notes, gold for manager notes (gold is the manager-notes
 *  semantic accent, not a brand color). */
function NoteComposer({
  call,
  currentMs,
  placeholder,
  accent,
}: {
  call: CallDetailModel;
  currentMs: number;
  placeholder: string;
  accent: "navy" | "gold";
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState("");

  // Anchor the note to the current audio position; <=0 → whole-call note.
  const atMs = currentMs > 0 ? Math.round(currentMs) : null;

  const addMutation = useMutation({
    mutationFn: (noteText: string) =>
      api.addCallNote(call.id, { start_ms: atMs, text: noteText }),
    onSuccess: () => {
      setText("");
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.notes(call.id),
      });
    },
  });

  const trimmed = text.trim();
  const canSubmit = trimmed.length > 0 && !addMutation.isPending;
  const submit = () => {
    if (!canSubmit) return;
    addMutation.mutate(trimmed);
  };

  const accentBtn =
    accent === "gold"
      ? "bg-band-fair text-white hover:opacity-90"
      : "bg-primary text-primary-foreground hover:opacity-90";

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-full border bg-muted px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground">
          {atMs !== null ? msToClock(atMs) : "—"}
        </span>
        <span className="text-xs text-muted-foreground">
          {atMs !== null ? "anchors to this moment" : "whole call"}
        </span>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        rows={3}
        disabled={addMutation.isPending}
        className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter is a convenient submit shortcut.
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className={`inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium transition disabled:pointer-events-none disabled:opacity-50 ${accentBtn}`}
        >
          {addMutation.isPending
            ? "Adding…"
            : atMs !== null
              ? `Submit note at ${msToClock(atMs)}`
              : "Submit note (whole call)"}
        </button>
        {addMutation.isError && (
          <p className="text-sm text-destructive">
            Couldn't add that note. Please try again.
          </p>
        )}
      </div>
    </div>
  );
}

/** "User Notes" card. Always shows the rep's own (source=self) notes. The
 *  composer renders ONLY for a staff viewer (canCompose) — a manager viewing
 *  this card sees it read-only. */
function UserNotesCard({
  call,
  currentMs,
  onSeek,
  canCompose,
}: {
  call: CallDetailModel;
  currentMs: number;
  onSeek: (ms: number) => void;
  canCompose: boolean;
}) {
  const notesQuery = useNotes(call.id);
  const notes = (notesQuery.data ?? []).filter((n) => n.source === "self");

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconUser className="h-3.5 w-3.5" />}
        title="User Notes"
        aside={!canCompose ? <Badge tone="muted">Read-only</Badge> : undefined}
      />

      {canCompose && (
        <NoteComposer
          call={call}
          currentMs={currentMs}
          placeholder="Add your note…"
          accent="navy"
        />
      )}

      <NotesList
        query={notesQuery}
        notes={notes}
        onSeek={onSeek}
        spaced={canCompose}
        emptyText={canCompose ? "No notes yet. Add the first one above." : "No user notes yet."}
      />
    </Card>
  );
}

/**
 * The note list under a composer. Empty/loading/error states stay SHORT; once
 * notes exist the list gets its own capped internal scroll so the card is
 * self-contained (Point 6) and never forces the whole column to scroll.
 */
function NotesList({
  query,
  notes,
  onSeek,
  spaced,
  emptyText,
}: {
  query: ReturnType<typeof useNotes>;
  notes: CallNote[];
  onSeek: (ms: number) => void;
  /** True when a composer sits above — adds a top divider/margin. */
  spaced: boolean;
  emptyText: string;
}) {
  return (
    <div className={spaced ? "mt-3 border-t pt-3" : ""}>
      {query.isLoading && (
        <p className="text-sm text-muted-foreground">Loading notes…</p>
      )}
      {query.isError && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn't load notes. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      )}
      {query.isSuccess && notes.length === 0 && (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      )}
      {notes.length > 0 && (
        <ul className="flex max-h-64 flex-col divide-y overflow-y-auto pr-1">
          {notes.map((note) => (
            <NoteRow key={note.id} note={note} onSeek={onSeek} />
          ))}
        </ul>
      )}
    </div>
  );
}

/** "Manager Notes" card. Composer renders ONLY for a manager/admin viewer.
 *  For a gated staff owner the whole card shows the locked placeholder (manager
 *  notes are hidden until they self-score) — and the server gates the GET too,
 *  so even the list is filtered. */
function ManagerNotesCard({
  call,
  currentMs,
  onSeek,
  canCompose,
  gated,
  coveredViewer = false,
}: {
  call: CallDetailModel;
  currentMs: number;
  onSeek: (ms: number) => void;
  canCompose: boolean;
  /** True when a staff owner hasn't self-scored yet — manager notes are hidden. */
  gated: boolean;
  /** Staff line owner on a covered call: manager notes are never theirs to see,
   *  so show neutral copy instead of asserting "No manager notes yet." */
  coveredViewer?: boolean;
}) {
  const notesQuery = useNotes(call.id);
  const notes = (notesQuery.data ?? []).filter((n) => n.source === "manager");

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconNote className="h-3.5 w-3.5" />}
        tone="gold"
        title="Manager Notes"
        aside={
          canCompose ? undefined : <Badge tone="muted">Read-only</Badge>
        }
      />

      {/* Composer: managers/admins only — staff never see a manager-note box. */}
      {canCompose && (
        <NoteComposer
          call={call}
          currentMs={currentMs}
          placeholder="Add manager note…"
          accent="gold"
        />
      )}

      {/* Gated staff owner → the same locked placeholder used across the screen.
          Covered-call line owner → neutral copy (the server withholds manager
          notes from them, so an empty list must not claim none exist). */}
      {gated ? (
        <LockedPlaceholder text="Your manager's notes are hidden until you submit your self-score. Your own notes are always visible." />
      ) : coveredViewer ? (
        <LockedPlaceholder
          text={`Manager notes on this call are visible to ${call.evaluated_rep_name ?? "the rep who handled it"} and managers.`}
        />
      ) : (
        <NotesList
          query={notesQuery}
          notes={notes}
          onSeek={onSeek}
          spaced={canCompose}
          emptyText={
            canCompose
              ? "No manager notes yet. Add the first one above."
              : "No manager notes yet."
          }
        />
      )}
    </Card>
  );
}

/** The shared "locked until self-score" placeholder used for gated content. */
function LockedPlaceholder({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed bg-muted/30 px-3 py-3 text-sm text-muted-foreground">
      <span aria-hidden className="mt-0.5">
        🔒
      </span>
      <span>{text}</span>
    </div>
  );
}

// ── Self-score (staff owner only; submitting unlocks the AI report) ─────────
/**
 * The defining product loop for a rep reviewing their OWN call: listen, then
 * grade yourself on the rubric BEFORE the AI is revealed. Only rendered for the
 * call owner acting as staff. Managers/admins are never gated and never see
 * the FORM — they get RepSelfScoreSection (the rep's submitted recap) instead.
 *
 * Two states from GET /api/calls/:id/self-score:
 *   - submitted (submitted_at set) → read-only recap. Once the AI report can be
 *     revealed we also show a SELF-vs-AI gap (totals + per-criterion deltas).
 *   - draft / none → the rubric FORM. The SUBMIT button is locked until the rep
 *     has listened to ≥90% of the call (coverage tracked in the parent); a
 *     progress bar shows how far they are. Submitting LOCKS the self-score and
 *     invalidates the detail / ai-reveal / coaching queries so the AI report and
 *     manager coaching unlock in place.
 */
const COVERAGE_GATE_PCT = 90;

function SelfScoreSection({
  call,
  coveragePct,
}: {
  call: CallDetailModel;
  coveragePct: number;
}) {
  const selfScoreQuery = useQuery({
    queryKey: queryKeys.calls.selfScore(call.id),
    queryFn: ({ signal }) => api.getSelfScore(call.id, signal),
  });

  if (selfScoreQuery.isLoading) {
    return (
      <Card>
        <SectionHeader
          icon={<IconUser className="h-3.5 w-3.5" />}
          title="Score yourself first"
        />
        <p className="text-sm text-muted-foreground">Loading your self-score…</p>
      </Card>
    );
  }

  if (selfScoreQuery.isError || !selfScoreQuery.data) {
    return (
      <Card>
        <SectionHeader
          icon={<IconUser className="h-3.5 w-3.5" />}
          title="Score yourself first"
        />
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn't load your self-score. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void selfScoreQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const selfScore = selfScoreQuery.data;

  if (selfScore.submitted_at) {
    return <SelfScoreSubmitted call={call} selfScore={selfScore} />;
  }

  return <SelfScoreForm call={call} selfScore={selfScore} coveragePct={coveragePct} />;
}

/**
 * Manager/admin view of the REP's self-score on someone else's call. The server
 * returns the evaluated rep's self_score (items + notes) to manager/admin
 * callers on the same GET, so this shares the SelfScoreSection query key (and
 * the AI-reveal scoreboard's cache). Read-only: managers never see the form.
 */
function RepSelfScoreSection({
  call,
  viewerIsEvaluatedRep,
}: {
  call: CallDetailModel;
  /** The manager/admin viewer IS the evaluated rep (their own call): the server
   *  returns THEIR self-score, so keep second-person copy — and never show the
   *  "Not submitted" nag (managers don't get the self-score form). */
  viewerIsEvaluatedRep: boolean;
}) {
  const selfScoreQuery = useQuery({
    queryKey: queryKeys.calls.selfScore(call.id),
    queryFn: ({ signal }) => api.getSelfScore(call.id, signal),
    // No evaluated rep -> there is no self-score to fetch or show.
    enabled: call.evaluated_rep_id != null,
  });

  if (call.evaluated_rep_id == null) return null;

  // Normalize "" to null so every fallback ("Rep" / "The rep") applies uniformly.
  const repName = call.evaluated_rep_name || null;
  const title = repName ? `${repName}'s self-score` : "Rep's self-score";

  // Self-scoring only happens on reviewed calls (it unlocks the AI reveal), so
  // an absent self-score on a never-reviewed call is expected — stay quiet
  // instead of implying the rep is behind on something.
  const reviewActive =
    call.review_requested || call.status === "scored" || call.ai_reveal?.score != null;

  if (selfScoreQuery.isLoading) {
    if (viewerIsEvaluatedRep || !reviewActive) return null;
    return (
      <Card className="shrink-0">
        <SectionHeader icon={<IconUser className="h-3.5 w-3.5" />} title={title} />
        <p className="text-sm text-muted-foreground">Loading self-score…</p>
      </Card>
    );
  }

  if (selfScoreQuery.isError || !selfScoreQuery.data) {
    if (viewerIsEvaluatedRep || !reviewActive) return null;
    return (
      <Card className="shrink-0">
        <SectionHeader icon={<IconUser className="h-3.5 w-3.5" />} title={title} />
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn't load the rep's self-score. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void selfScoreQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const selfScore = selfScoreQuery.data;

  if (!selfScore.submitted_at) {
    if (viewerIsEvaluatedRep || !reviewActive) return null;
    return (
      <Card className="shrink-0">
        <SectionHeader
          icon={<IconUser className="h-3.5 w-3.5" />}
          title={title}
          aside={<Badge tone="muted">Not submitted</Badge>}
        />
        <p className="text-sm text-muted-foreground">
          {repName ?? "The rep"} hasn't submitted a self-score for this call yet.
        </p>
      </Card>
    );
  }

  // viewerIsEvaluatedRep: omit repName so the recap stays second person
  // ("Your self-score") — it genuinely is the viewer's own.
  return (
    <SelfScoreSubmitted
      call={call}
      selfScore={selfScore}
      repName={viewerIsEvaluatedRep ? undefined : repName}
    />
  );
}

// — Submitted recap (read-only) + self-vs-AI gap once revealed —
// Rendered for the OWNER (their own recap) and, via RepSelfScoreSection, for a
// manager/admin reviewing the rep's submission — `repName` switches the copy
// from second person ("Your self-score") to the rep's name.
function SelfScoreSubmitted({
  call,
  selfScore,
  repName,
}: {
  call: CallDetailModel;
  selfScore: SelfScore;
  /** Set when a manager/admin is viewing: the rep's display name (or null to
   *  fall back to "Rep"). Undefined = the owner is viewing their own score. */
  repName?: string | null;
}) {
  const ownerView = repName === undefined;
  // Index rubric meta (label/weight) by key so the recap reads nicely even if
  // items came back in a different order than the rubric.
  const rubricByKey = useMemo(() => {
    const map = new Map<string, { label: string; weight: number; sort: number }>();
    for (const r of selfScore.rubric) {
      map.set(r.key, { label: r.label, weight: r.weight, sort: r.sort });
    }
    return map;
  }, [selfScore.rubric]);

  const items = useMemo(() => {
    return [...selfScore.items].sort(
      (a, b) =>
        (rubricByKey.get(a.criterion_key)?.sort ?? 0) -
        (rubricByKey.get(b.criterion_key)?.sort ?? 0),
    );
  }, [selfScore.items, rubricByKey]);

  // The AI reveal now succeeds for this rep (gate satisfied), so we can show the
  // gap. Enabled only once the call is scored; a 403/missing score just renders
  // nothing extra (the AiReviewSection below handles those states).
  const enabled = call.status === "scored" || call.ai_reveal?.score != null;
  const revealQuery = useQuery({
    queryKey: queryKeys.calls.aiReveal(call.id),
    queryFn: ({ signal }) => api.getAiReveal(call.id, signal),
    enabled,
    retry: false,
  });

  const aiReveal: AiReveal | undefined = revealQuery.data;
  const aiByKey = useMemo(() => {
    const map = new Map<string, { points: number; weight: number }>();
    if (aiReveal) {
      for (const it of aiReveal.items) {
        map.set(it.criterion_key, { points: it.points, weight: it.weight });
      }
    }
    return map;
  }, [aiReveal]);

  const aiTotal = aiReveal?.score?.total ?? null;
  const selfTotal = selfScore.total ?? 0;
  // §7.8: if the call was re-classified after this self-score, the two are on
  // different rubrics — suppress the (meaningless) self-vs-AI gap. Compare the
  // type the self-score was RECORDED under against the AI's scored type.
  const mismatch =
    aiReveal?.score?.call_type != null &&
    selfScore.self_score_call_type != null &&
    aiReveal.score.call_type !== selfScore.self_score_call_type;
  const showGap = aiTotal != null && !mismatch;

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconUser className="h-3.5 w-3.5" />}
        title={ownerView ? "Your self-score" : `${repName ?? "Rep"}'s self-score`}
        aside={<Badge tone="muted">Submitted</Badge>}
      />

      {/* Totals row: self alone, or self-vs-AI once the AI report is available. */}
      <div className="flex flex-wrap items-baseline gap-4">
        <div className="flex flex-col">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {ownerView ? "You" : "Self"}
          </span>
          <span className={`text-2xl font-semibold tabular-nums ${scoreTextClass(selfTotal)}`}>
            {selfTotal}
            <span className="text-base font-normal text-muted-foreground">/100</span>
          </span>
        </div>
        {showGap && (
          <>
            <div className="flex flex-col">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                AI
              </span>
              <span className={`text-2xl font-semibold tabular-nums ${scoreTextClass(aiTotal as number)}`}>
                {aiTotal}
                <span className="text-base font-normal text-muted-foreground">/100</span>
              </span>
            </div>
            <GapBadge
              delta={selfTotal - (aiTotal as number)}
              subject={ownerView ? "You" : (repName ?? "Rep")}
            />
          </>
        )}
      </div>

      {mismatch && (
        <p className="mt-2 flex items-start gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <IconInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {ownerView
              ? "This call was re-classified after you self-scored, so your score and the AI score are on different rubrics — the comparison is hidden."
              : "This call was re-classified after the rep self-scored, so the self-score and the AI score are on different rubrics — the comparison is hidden."}
          </span>
        </p>
      )}

      {/* Per-criterion recap (+ delta column once the AI report is in). */}
      <ul className="mt-4 flex flex-col gap-3 border-t pt-3">
        {items.map((item) => {
          const meta = rubricByKey.get(item.criterion_key);
          const weight = meta?.weight ?? 0;
          const label = meta?.label ?? item.criterion_key;
          const ai = aiByKey.get(item.criterion_key);
          return (
            <li key={item.criterion_key} className="flex flex-col gap-1">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium">{label}</span>
                <div className="flex items-center gap-2 text-sm tabular-nums">
                  <span className="font-semibold">
                    {item.points}
                    <span className="font-normal text-muted-foreground">/{weight}</span>
                  </span>
                  {ai && (
                    <>
                      <span className="text-muted-foreground">vs AI</span>
                      <span className="font-semibold">
                        {ai.points}
                        <span className="font-normal text-muted-foreground">/{ai.weight}</span>
                      </span>
                      <GapBadge delta={item.points - ai.points} small />
                    </>
                  )}
                </div>
              </div>
              {item.note && (
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {item.note}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

/** Small badge for a self-vs-AI delta. Positive = the rep graded themselves
 *  higher than the AI; zero = matched. `subject` is "You" for the owner's own
 *  recap, or the rep's name when a manager/admin is viewing. */
function GapBadge({
  delta,
  small,
  subject = "You",
}: {
  delta: number;
  small?: boolean;
  subject?: string;
}) {
  if (delta === 0) {
    return <Badge tone="success">{small ? "match" : "Matched the AI"}</Badge>;
  }
  const sign = delta > 0 ? "+" : "";
  return (
    <Badge tone={delta > 0 ? "warning" : "primary"}>
      {small
        ? `${sign}${delta}`
        : `${subject} ${delta > 0 ? "over" : "under"}-rated by ${Math.abs(delta)}`}
    </Badge>
  );
}

// — Rubric self-score form (gated on listen coverage) —
function SelfScoreForm({
  call,
  selfScore,
  coveragePct,
}: {
  call: CallDetailModel;
  selfScore: SelfScore;
  coveragePct: number;
}) {
  const queryClient = useQueryClient();

  const rubric = useMemo(
    () => [...selfScore.rubric].sort((a, b) => a.sort - b.sort),
    [selfScore.rubric],
  );

  // Seed editable points + notes from any existing draft, then keep local.
  const draftByKey = useMemo(() => {
    const map = new Map<string, { points: number; note: string }>();
    for (const it of selfScore.items) {
      map.set(it.criterion_key, { points: it.points, note: it.note ?? "" });
    }
    return map;
  }, [selfScore.items]);

  const [points, setPoints] = useState<Record<string, number>>(() => {
    const next: Record<string, number> = {};
    for (const r of rubric) next[r.key] = draftByKey.get(r.key)?.points ?? 0;
    return next;
  });
  const [notes, setNotes] = useState<Record<string, string>>(() => {
    const next: Record<string, string> = {};
    for (const r of rubric) next[r.key] = draftByKey.get(r.key)?.note ?? "";
    return next;
  });

  const total = useMemo(
    () => rubric.reduce((sum, r) => sum + (points[r.key] || 0), 0),
    [rubric, points],
  );

  const submitMutation = useMutation({
    mutationFn: () =>
      api.submitSelfScore(call.id, {
        items: rubric.map((r) => {
          const note = (notes[r.key] ?? "").trim();
          return {
            criterion_key: r.key,
            points: points[r.key] ?? 0,
            note: note.length > 0 ? note : undefined,
          };
        }),
        // Coverage is a 0–1 fraction; we track an integer percentage.
        listen_coverage: coveragePct / 100,
      }),
    onSuccess: () => {
      // Flip the gate: the self-score is now locked, so the AI report + manager
      // coaching unlock for this rep. Refetch everything that depends on it.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.selfScore(call.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.detail(call.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.aiReveal(call.id),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.coaching(call.id),
      });
      // Manager notes are gated too — refetch so they appear now.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.notes(call.id),
      });
    },
  });

  const coverageMet = coveragePct >= COVERAGE_GATE_PCT;
  const canSubmit = coverageMet && !submitMutation.isPending;

  return (
    <Card className="flex flex-col xl:min-h-0">
      <SectionHeader
        icon={<IconUser className="h-3.5 w-3.5" />}
        title="Score yourself first"
        info="Listen to the whole call, then grade yourself on each point. Submitting locks your score and unlocks the AI review and coaching."
      />

      {/* §7.4: the call type isn't classified yet, so the form is showing the
          default (tint) rubric. Warn the rep before they grade. */}
      {selfScore.rubric_pending && (
        <p className="mb-2 flex items-start gap-1.5 rounded-md bg-band-fair/10 px-2 py-1 text-xs text-band-fair">
          <IconInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Still detecting this call's type — showing the standard tint rubric. If it
            looks wrong, ask a manager to set the call type before you grade.
          </span>
        </p>
      )}

      {/* Coverage line: ALWAYS visible, with the lock note shown only until ≥90%
          is heard. The Submit button stays gated on this. (Brand navy fill.) */}
      <div className="flex flex-col gap-1.5 rounded-md border bg-primary/5 p-2.5">
        <p className="text-sm">
          Listen to the full call to self-score —{" "}
          <span className="font-semibold tabular-nums">{coveragePct}%</span> listened
        </p>
        <div
          className="h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={coveragePct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-primary transition-[width] duration-300"
            style={{ width: `${coveragePct}%` }}
          />
        </div>
        {!coverageMet && (
          <p className="text-xs text-muted-foreground">
            The Submit button unlocks once you've heard at least{" "}
            {COVERAGE_GATE_PCT}% of the recording.
          </p>
        )}
      </div>

      {/* Dense rubric: a slider + points per criterion; the optional "why this
          score?" note is collapsed by default so all four rows fit. Scrolls
          internally on tall viewports so the total + Submit stay reachable. */}
      <ul className="mt-3 flex flex-col gap-3 overflow-y-auto border-t pt-3 xl:min-h-0 xl:flex-1">
        {rubric.map((r) => (
          <SelfScoreRow
            key={r.key}
            label={r.label}
            weight={r.weight}
            fullCredit={r.full_credit ?? null}
            value={points[r.key] ?? 0}
            note={notes[r.key] ?? ""}
            disabled={submitMutation.isPending}
            onPointsChange={(v) => setPoints((prev) => ({ ...prev, [r.key]: v }))}
            onNoteChange={(v) => setNotes((prev) => ({ ...prev, [r.key]: v }))}
          />
        ))}
      </ul>

      {/* Sticky footer: running total + Submit stay pinned to the card bottom
          even when the rubric above scrolls. */}
      <div className="sticky bottom-0 mt-3 flex flex-col gap-2 border-t bg-card pt-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Self Score</span>
          <span className={`text-lg font-semibold tabular-nums ${scoreTextClass(total)}`}>
            {total}
            <span className="text-sm font-normal text-muted-foreground">/100</span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => submitMutation.mutate()}
            disabled={!canSubmit}
            title={coverageMet ? undefined : "Listen to the full call before submitting"}
            className="inline-flex items-center justify-center rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground transition hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
          >
            {submitMutation.isPending ? "Submitting…" : "Submit Self Report"}
          </button>
          {!coverageMet && (
            <span className="text-xs text-muted-foreground">
              Locked until you've listened to the full call.
            </span>
          )}
        </div>
        {submitMutation.isError && (
          <p className="text-sm text-destructive">
            Couldn't submit your self-score. Please try again.
          </p>
        )}
      </div>
    </Card>
  );
}

/** One dense self-score rubric row: label + value, a slider, and a COLLAPSIBLE
 *  optional note (hidden by default so all four criteria fit without scrolling
 *  the whole form). */
function SelfScoreRow({
  label,
  weight,
  value,
  note,
  fullCredit,
  disabled,
  onPointsChange,
  onNoteChange,
}: {
  label: string;
  weight: number;
  value: number;
  note: string;
  /** §4 "full credit" anchor — shown so the rep grades to the bar (§7.4). */
  fullCredit?: string | null;
  disabled: boolean;
  onPointsChange: (v: number) => void;
  onNoteChange: (v: string) => void;
}) {
  // Auto-open the note when a draft already has text, otherwise keep it tucked.
  const [noteOpen, setNoteOpen] = useState(note.trim().length > 0);
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-sm font-medium" title={label}>
          {label}
        </span>
        <span className="shrink-0 text-sm font-semibold tabular-nums">
          {value}
          <span className="font-normal text-muted-foreground"> / {weight}</span>
        </span>
      </div>
      {fullCredit && (
        <p className="text-xs leading-snug text-muted-foreground">
          <span className="font-medium text-foreground">Full marks:</span> {fullCredit}
        </p>
      )}
      <input
        type="range"
        min={0}
        max={weight}
        step={1}
        value={value}
        disabled={disabled}
        onChange={(e) => onPointsChange(Number(e.target.value))}
        className="w-full accent-primary disabled:opacity-60"
        aria-label={`${label} points`}
      />
      {noteOpen ? (
        <textarea
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="Why this score? (optional)"
          rows={2}
          disabled={disabled}
          autoFocus={note.trim().length === 0}
          className="w-full resize-y rounded-md border bg-background px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        />
      ) : (
        <button
          type="button"
          onClick={() => setNoteOpen(true)}
          disabled={disabled}
          className="self-start text-xs font-medium text-primary hover:underline disabled:opacity-60"
        >
          + Add note
        </button>
      )}
    </li>
  );
}

// ── AI review & coaching (gated; carries the Trigger-AI-Review button) ───────
const STATUS_LABELS: Record<PipelineStatus, string> = {
  discovered: "Queued",
  recording_ready: "Fetching recording",
  transcribing: "Transcribing",
  transcribed: "Transcribed",
  scoring: "AI scoring",
  scored: "Scored",
  error: "Error",
  skipped: "Skipped",
};

function confidenceTone(
  confidence: string | null,
): "success" | "warning" | "danger" | "muted" {
  switch (confidence) {
    case "high":
      return "success";
    case "medium":
      return "warning";
    case "low":
      return "danger";
    default:
      return "muted";
  }
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-muted-foreground"
    />
  );
}

/**
 * The AI Review & Coaching card. This single card now also owns the
 * Trigger-AI-Review action + the processing status, because the mockup folds
 * "queue the AI" into this card.
 *
 * State machine (per the Map):
 *   • call.status === "scored" (or detail already carries a score) → render the
 *     AI body, GATED for un-self-scored staff (403 → locked placeholder).
 *   • call is flagged + still in-flight → "Queued / Processing" (the parent
 *     polls the detail every 5s, so this advances on its own).
 *   • call is flagged but errored/skipped → a small status note.
 *   • call has NO AI review yet and is NOT queued → a clear "Trigger AI Review"
 *     button that calls setReviewFlag(true) to enqueue transcription + scoring.
 *     For a gated staff owner we ALSO show the locked placeholder (the result
 *     stays hidden behind their self-score even after they trigger it).
 */
function AiReviewSection({
  call,
  onSeek,
  gated,
  ownerView,
  canReadSelfScore,
  coveredViewer = false,
}: {
  call: CallDetailModel;
  onSeek: (ms: number) => void;
  gated: boolean;
  /** The self-score shown belongs to the viewer (owner, or a manager/admin who
   *  is the evaluated rep) — keeps the mismatch note in the right person. */
  ownerView: boolean;
  /** False for a staff LINE OWNER viewing a covered call: the self-score GET
   *  would 403 (it's the covering rep's), so don't fetch it at all. */
  canReadSelfScore: boolean;
  /** Staff line owner on a covered call — can't trigger a review or unlock
   *  anything here, so the "use Trigger AI Review" nudge must not show. */
  coveredViewer?: boolean;
}) {
  // Fetch once the call is scored (or already carries a score from the detail).
  const hasScore = call.ai_reveal?.score != null;
  const enabled = call.status === "scored" || hasScore;

  const revealQuery = useQuery({
    queryKey: queryKeys.calls.aiReveal(call.id),
    queryFn: ({ signal }) => api.getAiReveal(call.id, signal),
    enabled,
  });

  // The submitted self-score (if any), so the scoreboard header can show
  // Self / AI / delta together. Shares the SelfScoreSection query key, so for a
  // staff owner this is already cached; managers may have no self-score (null
  // total → no comparison shown). Only fetched once the gate is open.
  const selfScoreQuery = useQuery({
    queryKey: queryKeys.calls.selfScore(call.id),
    queryFn: ({ signal }) => api.getSelfScore(call.id, signal),
    enabled: enabled && !gated && canReadSelfScore,
    retry: false,
  });
  // §7.8 self-score rubric-mismatch suppression: if a manager re-classified the
  // call AFTER the rep self-scored, the (immutable) self-score is on a different
  // rubric than the AI score. Comparing them is meaningless, so suppress the
  // Self / delta display and show a note instead.
  const selfData = selfScoreQuery.data;
  const revealScore = revealQuery.data?.score ?? null;
  // Compare the type the self-score was RECORDED under (self_score_call_type)
  // against the type the AI scored under — only a real re-classification differs.
  const selfMismatch =
    selfData?.submitted_at != null &&
    revealScore != null &&
    selfData.self_score_call_type != null &&
    revealScore.call_type != null &&
    selfData.self_score_call_type !== revealScore.call_type;
  const selfTotal =
    selfData?.submitted_at != null && !selfMismatch ? selfData.total : null;

  // The review is enqueued from the header "Trigger AI Review" action now; this
  // card only reflects the resulting status (queued / processing / scored / locked).
  const requested = call.review_requested;

  // 403 = the self-score gate isn't satisfied yet (staff must self-score first).
  const isGated =
    gated ||
    (revealQuery.error instanceof ApiRequestError && revealQuery.error.status === 403);

  const processing = requested && isInFlight(call.status);
  const noReviewYet = !requested && !enabled;

  return (
    <Card>
      <SectionHeader
        icon={<IconBrain className="h-3.5 w-3.5" />}
        title="AI Review & Coaching"
        aside={
          isGated ? (
            <Badge tone="warning">{ownerView ? "Unlocked after self-score" : "Locked"}</Badge>
          ) : undefined
        }
      />

      {/* GATE: staff owner who hasn't self-scored → locked placeholder. The work
          can still be queued (below), but the result stays hidden. A line owner
          viewing a COVERED call (not theirs to score) gets neutral copy — they
          can never unlock this review. */}
      {isGated && (
        <LockedPlaceholder
          text={
            ownerView
              ? "Submit your self-score above to unlock the AI review and coaching for this call."
              : `This call's review belongs to ${call.evaluated_rep_name ?? "the rep who handled it"} — only they and managers can see it.`
          }
        />
      )}

      {/* No AI review yet and nothing queued → point to the header action.
          A covered-call line owner has no such action — neutral copy instead. */}
      {noReviewYet && (
        <p className={`text-sm text-muted-foreground ${isGated ? "mt-3" : ""}`}>
          {coveredViewer ? (
            <>
              This call hasn't been reviewed by AI yet.{" "}
              {call.evaluated_rep_name ?? "The rep who handled it"} or a manager can
              queue its review.
            </>
          ) : (
            <>
              This call hasn't been reviewed by AI yet. Use{" "}
              <span className="font-medium text-foreground">Trigger AI Review</span> in
              the header to queue transcription and AI coaching.
            </>
          )}
        </p>
      )}

      {/* Flagged + still moving through the pipeline → live status. */}
      {processing && (
        <div className={isGated ? "mt-3" : ""}>
          <div className="flex items-center gap-2">
            <Spinner />
            <Badge tone="primary">{STATUS_LABELS[call.status] ?? call.status}</Badge>
            <span className="text-sm text-muted-foreground">
              Queued — this updates on its own.
            </span>
          </div>
        </div>
      )}

      {/* Flagged but terminal-failed states. */}
      {requested && call.status === "error" && (
        <p className={`text-sm text-destructive ${isGated ? "mt-3" : ""}`}>
          Something went wrong while processing this call. Try toggling the review
          off and on, or contact an admin if it keeps failing.
        </p>
      )}
      {requested && call.status === "skipped" && (
        <p className={`text-sm text-muted-foreground ${isGated ? "mt-3" : ""}`}>
          This call was skipped (no usable recording or excluded from scoring).
        </p>
      )}

      {/* The actual AI report — only when the gate is open AND we have a score. */}
      {!isGated && enabled && (
        <>
          {revealQuery.isLoading && (
            <p className="text-sm text-muted-foreground">Loading AI review…</p>
          )}

          {revealQuery.isError && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-destructive">
                We couldn't load the AI review. Please try again.
              </p>
              <div>
                <Button variant="secondary" onClick={() => void revealQuery.refetch()}>
                  Try again
                </Button>
              </div>
            </div>
          )}

          {revealQuery.isSuccess && revealQuery.data.score == null && (
            <p className="text-sm text-muted-foreground">
              Not scored yet — processing may still be in progress.
            </p>
          )}

          {revealQuery.isSuccess && revealQuery.data.score != null && (
            <AiReviewBody
              reveal={revealQuery.data}
              onSeek={onSeek}
              selfTotal={selfTotal}
              selfMismatch={selfMismatch}
              ownerView={ownerView}
            />
          )}
        </>
      )}
    </Card>
  );
}

/**
 * AI-score feedback widget (CLAUDE.md §8 — rate every production AI score). A
 * small overall 👍/👎 plus an optional note, posting to api.submitFeedback with
 * the production scoring run's `audit_id`. Disabled (with a hint) when audit_id
 * is null — i.e. no resolvable production run to attach feedback to.
 *
 * Mutation-only (no query): we keep the selected rating + a "thanks" confirmation
 * in local state and re-submit (UPSERT server-side) when the rating changes, so
 * re-rating just updates. There is nothing to invalidate — feedback isn't read
 * back into any screen — so this never setQueryData's the response.
 */
function FeedbackWidget({ auditId }: { auditId: string | null }) {
  // The rating currently chosen in the UI (optimistic — set on click).
  const [selected, setSelected] = useState<"up" | "down" | null>(null);
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState("");

  const feedbackMutation = useMutation({
    mutationFn: (body: { rating: "up" | "down"; note?: string }) =>
      api.submitFeedback({
        audit_id: auditId as string,
        rating: body.rating,
        note: body.note,
      }),
  });

  const disabled = auditId === null;

  function rate(rating: "up" | "down") {
    if (disabled) return;
    setSelected(rating);
    feedbackMutation.mutate({ rating, note: note.trim() || undefined });
  }

  function submitNote() {
    if (disabled || !selected) return;
    feedbackMutation.mutate({ rating: selected, note: note.trim() || undefined });
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-muted/20 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">
          Was this AI review helpful?
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => rate("up")}
            disabled={disabled || feedbackMutation.isPending}
            aria-pressed={selected === "up"}
            title="This review was helpful"
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-sm transition disabled:pointer-events-none disabled:opacity-40 ${
              selected === "up"
                ? "border-band-strong/40 bg-band-strong/15 text-band-strong"
                : "hover:bg-accent"
            }`}
          >
            👍
          </button>
          <button
            type="button"
            onClick={() => rate("down")}
            disabled={disabled || feedbackMutation.isPending}
            aria-pressed={selected === "down"}
            title="This review missed the mark"
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md border text-sm transition disabled:pointer-events-none disabled:opacity-40 ${
              selected === "down"
                ? "border-band-weak/40 bg-band-weak/15 text-band-weak"
                : "hover:bg-accent"
            }`}
          >
            👎
          </button>
        </div>
        {!disabled && selected && (
          <button
            type="button"
            onClick={() => setShowNote((v) => !v)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {showNote ? "Hide note" : "Add a note"}
          </button>
        )}
        {feedbackMutation.isSuccess && !feedbackMutation.isPending && (
          <span className="text-xs text-band-strong">Thanks for the feedback!</span>
        )}
      </div>

      {disabled && (
        <p className="text-xs text-muted-foreground">
          Feedback isn&apos;t available for this review yet.
        </p>
      )}

      {!disabled && selected && showNote && (
        <div className="flex flex-col gap-2">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What did the AI get right or wrong? (optional)"
            rows={2}
            disabled={feedbackMutation.isPending}
            className="w-full resize-y rounded-md border bg-background px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={submitNote}
              disabled={feedbackMutation.isPending}
            >
              {feedbackMutation.isPending ? "Saving…" : "Save note"}
            </Button>
            {feedbackMutation.isError && (
              <span className="text-xs text-destructive">
                Couldn&apos;t save that. Please try again.
              </span>
            )}
          </div>
        </div>
      )}

      {!disabled && feedbackMutation.isError && !showNote && (
        <p className="text-xs text-destructive">
          Couldn&apos;t save your feedback. Please try again.
        </p>
      )}
    </div>
  );
}

/** One rubric item reshaped for the scoreboard (no backend change). */
interface ScoreRow {
  key: string;
  label: string;
  weight: number;
  points: number;
  confidence: string | null;
  rationale: string;
  coaching_tip: string;
  evidence_ms: number[];
  /** points/weight as a 0–1 ratio (1 = full marks); 0 if weight is 0. */
  ratio: number;
  /** Full marks on a weighted criterion → a "strength". */
  isStrength: boolean;
  /** Below ~70% of available points → visually flagged "needs attention". */
  needsAttention: boolean;
}

const ATTENTION_RATIO = 0.7;

/**
 * SCAN-FIRST AI scoreboard (Points 3 + 4).
 *
 * Layout, top to bottom:
 *   1. Scoreboard HEADER — AI overall score + confidence + needs-review badge,
 *      and (when a self-score exists) Self / AI / delta together.
 *   2. MOST-USEFUL GUIDANCE up top — a short AI summary + the TOP 2–3 coaching
 *      suggestions (drawn from the weakest criteria that have a tip).
 *   3. A compact rubric TABLE — one row per criterion (label · points/weight ·
 *      confidence · one-line rationale · evidence chips), weakest-first, with a
 *      "needs attention" marker on low rows. The full rationale + coaching tip
 *      EXPAND inline per row (accordion) so they don't always eat vertical space.
 *
 * All of this is reshaped client-side from `reveal.items` — no backend change.
 */
function AiReviewBody({
  reveal,
  onSeek,
  selfTotal,
  selfMismatch = false,
  ownerView = true,
}: {
  reveal: AiReveal;
  onSeek: (ms: number) => void;
  /** Submitted self-score total, or null when there's none to compare against. */
  selfTotal: number | null;
  /** True when a self-score exists but is on a different rubric (§7.8) — the
   *  per-criterion / delta comparison is suppressed and a note is shown instead. */
  selfMismatch?: boolean;
  /** False when a manager/admin is viewing someone else's call — the self-score
   *  is the REP's, so the mismatch note must not say "your". */
  ownerView?: boolean;
}) {
  const score = reveal.score!;

  // Reshape items → scoreRows (Point 4). Keep the original rubric order for the
  // table; derive a weakest-first list for the "top suggestions" up top.
  const scoreRows: ScoreRow[] = useMemo(
    () =>
      reveal.items.map((it) => {
        const ratio = it.weight > 0 ? it.points / it.weight : 1;
        return {
          key: it.criterion_key,
          label: it.label,
          weight: it.weight,
          points: it.points,
          confidence: it.confidence,
          rationale: it.rationale,
          coaching_tip: it.coaching_tip,
          evidence_ms: it.evidence_ms,
          ratio,
          // "Did well" = scored at/above the attention bar (reinforce these up top).
          isStrength: it.weight > 0 && ratio >= ATTENTION_RATIO,
          needsAttention: it.weight > 0 && ratio < ATTENTION_RATIO,
        };
      }),
    [reveal.items],
  );

  const weakestRows = useMemo(
    () => [...scoreRows].sort((a, b) => a.ratio - b.ratio),
    [scoreRows],
  );
  // Top 2–3 actionable suggestions: weakest criteria that actually carry a tip.
  const topSuggestions = useMemo(
    () => weakestRows.filter((r) => r.coaching_tip.trim().length > 0).slice(0, 3),
    [weakestRows],
  );
  // "What went well" — strongest-first, surfaced at the TOP to reinforce.
  const wentWell = useMemo(
    () => scoreRows.filter((r) => r.isStrength).sort((a, b) => b.ratio - a.ratio),
    [scoreRows],
  );

  const delta = selfTotal != null ? selfTotal - score.total : null;

  return (
    <div className="flex flex-col gap-3">
      {/* 1 — Scoreboard header: AI score (+ Self / delta when present). */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border bg-primary/5 p-3">
        <div className="flex flex-col">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            AI Score
          </span>
          <span className={`text-2xl font-bold leading-none tabular-nums ${scoreTextClass(score.total)}`}>
            {score.total}
            <span className="text-sm font-normal text-muted-foreground">/100</span>
          </span>
          <span className={`text-[11px] font-medium ${scoreTextClass(score.total)}`}>
            {scoreBandLabel(score.total)}
          </span>
        </div>

        {selfTotal != null && (
          <>
            <span className="h-9 w-px bg-border" aria-hidden />
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Self
              </span>
              <span className="text-2xl font-bold leading-none tabular-nums text-foreground">
                {selfTotal}
                <span className="text-sm font-normal text-muted-foreground">/100</span>
              </span>
              {delta != null && (
                <span className="text-[11px]">
                  <GapBadge delta={delta} small />
                </span>
              )}
            </div>
          </>
        )}

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {score.needs_review && <Badge tone="warning">Needs review</Badge>}
          {score.scoring_confidence && (
            <Badge tone={confidenceTone(score.scoring_confidence)}>
              {score.scoring_confidence} confidence
            </Badge>
          )}
        </div>
      </div>

      {selfMismatch && (
        <p className="-mt-1 flex items-start gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground">
          <IconInfo className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {ownerView
              ? "Your earlier self-score was on a different call type, so the self vs AI comparison is hidden for this call."
              : "The rep's earlier self-score was on a different call type, so the self vs AI comparison is hidden for this call."}
          </span>
        </p>
      )}

      {/* CLAUDE.md §8: rate this production AI score (👍/👎 + optional note). */}
      <FeedbackWidget auditId={reveal.audit_id} />

      {/* 2 — POSITIVE FIRST: what went well, to reinforce the behavior. */}
      {wentWell.length > 0 && (
        <div className="rounded-md border border-band-strong/30 bg-band-strong/5 p-3">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-band-strong">
            <IconCheckCircle className="h-3.5 w-3.5" /> What went well
          </h3>
          <ul className="flex flex-col gap-1.5">
            {wentWell.map((s) => (
              <li key={s.key} className="flex items-start gap-2 text-sm">
                <IconCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-band-strong" />
                <span className="min-w-0">
                  <span className="font-medium">{s.label}</span>
                  <span className="text-muted-foreground"> — {s.points}/{s.weight}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 3 — DO DIFFERENTLY: action-oriented bullets. The coaching lives ONLY here
          (not duplicated in the rubric rows below — those carry the analysis). */}
      {topSuggestions.length > 0 && (
        <div className="rounded-md border border-band-fair/30 bg-band-fair/5 p-3">
          <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-band-fair">
            <IconBulb className="h-3.5 w-3.5" /> Do differently next time
          </h3>
          <ul className="flex flex-col gap-2">
            {topSuggestions.map((s) => (
              <li key={s.key} className="flex items-start gap-2 text-sm">
                <IconBulb className="mt-0.5 h-4 w-4 shrink-0 text-band-fair" />
                <span className="min-w-0">
                  <span className="font-medium">{s.label}: </span>
                  {s.coaching_tip}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 3 — Compact rubric scoreboard (weakest-first), rows expand on click.
          Each row carries its own 👍/👎 (criterion-level feedback, §8). */}
      <div className="flex flex-col">
        <div className="flex items-center justify-between px-1 pb-1">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Rubric
          </h3>
          <span className="text-[11px] text-muted-foreground">Tap a row for detail</span>
        </div>
        <ul className="flex flex-col divide-y rounded-md border">
          {weakestRows.map((row) => (
            <RubricScoreRow
              key={row.key}
              row={row}
              onSeek={onSeek}
              auditId={reveal.audit_id}
            />
          ))}
        </ul>
      </div>

      {/* Full AI narrative — secondary, collapsed by default (the bullets above
          carry the action; this is here for the full read). */}
      {score.summary && (
        <details className="group rounded-md border px-3 py-2">
          <summary className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <IconChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
            AI summary
          </summary>
          <p className="mt-2 text-sm text-muted-foreground">{score.summary}</p>
        </details>
      )}
    </div>
  );
}

/**
 * Per-criterion 👍/👎 (CLAUDE.md §8 — criterion-level feedback on the AI
 * review). Posts to the SAME /api/feedback endpoint as the overall widget,
 * with `criterion_key` set: the server UPSERTs one vote per (audit_id,
 * criterion_key, user), so re-rating just updates. Disabled (dimmed) when
 * there is no resolvable audit row — mirroring the overall widget's gating.
 * Lives OUTSIDE the row's expand <button> (no nested buttons).
 */
function CriterionFeedback({
  auditId,
  criterionKey,
  label,
}: {
  auditId: string | null;
  criterionKey: string;
  /** Human criterion name (row.label) — used for aria-labels/tooltips so a
   * screen reader hears "Offered the upsell", never the raw snake_case key. */
  label: string;
}) {
  const [selected, setSelected] = useState<"up" | "down" | null>(null);
  const feedbackMutation = useMutation({
    mutationFn: (rating: "up" | "down") =>
      api.submitFeedback({
        audit_id: auditId as string,
        rating,
        criterion_key: criterionKey,
      }),
    // Roll the optimistic selection back so a failed vote isn't shown as saved.
    onError: () => setSelected(null),
  });

  const disabled = auditId === null;

  function rate(rating: "up" | "down") {
    if (disabled || feedbackMutation.isPending) return;
    setSelected(rating);
    feedbackMutation.mutate(rating);
  }

  return (
    <span
      className="flex shrink-0 items-center gap-1 px-2 py-2"
      title={
        disabled
          ? "Feedback isn't available for this review yet."
          : "Did the AI get this criterion right?"
      }
    >
      <button
        type="button"
        onClick={() => rate("up")}
        disabled={disabled || feedbackMutation.isPending}
        aria-pressed={selected === "up"}
        aria-label={`The AI got "${label}" right`}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-xs transition disabled:pointer-events-none disabled:opacity-40 ${
          selected === "up"
            ? "border-band-strong/40 bg-band-strong/15"
            : "hover:bg-accent"
        }`}
      >
        👍
      </button>
      <button
        type="button"
        onClick={() => rate("down")}
        disabled={disabled || feedbackMutation.isPending}
        aria-pressed={selected === "down"}
        aria-label={`The AI got "${label}" wrong`}
        className={`inline-flex h-6 w-6 items-center justify-center rounded-md border text-xs transition disabled:pointer-events-none disabled:opacity-40 ${
          selected === "down"
            ? "border-band-weak/40 bg-band-weak/15"
            : "hover:bg-accent"
        }`}
      >
        👎
      </button>
      {feedbackMutation.isError && (
        <span className="text-[10px] text-destructive" role="alert">
          Couldn&apos;t save — tap again
        </span>
      )}
    </span>
  );
}

/**
 * One scoreboard row: the always-visible scan line (attention marker · label ·
 * points/weight · confidence · one-line rationale · per-criterion 👍/👎) plus
 * an inline-expanding detail area (full rationale + coaching tip + evidence
 * chips). Evidence-chip seek (onSeek) is preserved exactly.
 */
function RubricScoreRow({
  row,
  onSeek,
  auditId,
}: {
  row: ScoreRow;
  onSeek: (ms: number) => void;
  /** The production scoring run's audit row (null disables the thumbs). */
  auditId: string | null;
}) {
  const [open, setOpen] = useState(false);
  // The coaching tip is surfaced once, in "Do differently" at the top — the row
  // detail carries the ANALYSIS (rationale + evidence) so it stands on its own.
  const hasDetail =
    row.rationale.trim().length > 0 || row.evidence_ms.length > 0;

  return (
    <li
      className={`flex flex-col ${row.needsAttention ? "bg-band-weak/5" : ""}`}
    >
      {/* Scan line: the expand button fills the row; the thumbs sit BESIDE it
          (not inside — nested buttons are invalid HTML). */}
      <div className="flex w-full items-start">
        <button
          type="button"
          onClick={() => hasDetail && setOpen((v) => !v)}
          aria-expanded={open}
          className={`flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left transition-colors ${
            hasDetail ? "hover:bg-accent/40" : "cursor-default"
          }`}
        >
          {/* attention marker keeps low rows visible at a glance */}
          <span className="mt-0.5 shrink-0">
            {row.needsAttention ? (
              <IconAlertTriangle className="h-4 w-4 text-band-weak" />
            ) : row.isStrength ? (
              <IconCheckCircle className="h-4 w-4 text-band-strong" />
            ) : (
              <span className="inline-block h-4 w-4" />
            )}
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium" title={row.label}>
                {row.label}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <span className={`text-sm font-semibold tabular-nums ${row.needsAttention ? "text-band-weak" : ""}`}>
                  {row.points}
                  <span className="font-normal text-muted-foreground">/{row.weight}</span>
                </span>
                {row.confidence && (
                  <Badge tone={confidenceTone(row.confidence)}>{row.confidence}</Badge>
                )}
                {hasDetail && (
                  <IconChevronDown
                    className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
                  />
                )}
              </span>
            </div>
            {/* one-line rationale preview (clamped) — full text on expand */}
            {row.rationale && !open && (
              <span className="truncate text-xs text-muted-foreground">
                {row.rationale}
              </span>
            )}
          </div>
        </button>
        <CriterionFeedback auditId={auditId} criterionKey={row.key} label={row.label} />
      </div>

      {/* Expanded detail: full rationale + coaching tip + evidence chips. */}
      {open && hasDetail && (
        <div className="flex flex-col gap-2 px-3 pb-3 pl-9">
          {row.rationale && <p className="text-sm">{row.rationale}</p>}
          {row.evidence_ms.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Evidence:</span>
              {row.evidence_ms.map((ms, i) => (
                <button
                  key={`${row.key}-${i}-${ms}`}
                  type="button"
                  onClick={() => onSeek(ms)}
                  className="inline-flex items-center rounded-full border bg-muted px-2 py-0.5 font-mono text-xs tabular-nums text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  title="Play from this moment"
                >
                  {msToClock(ms)}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

// ── Coaching display (visible to EVERYONE) ──────────────────────────────────
/**
 * Surfaces the manager's coaching on this call to whoever can view it:
 *   - a manager OVERRIDE (the final score of record) renders as a prominent
 *     "Manager-adjusted score" card with the required note + per-item points;
 *   - a FLAG (exemplary / needs-work) renders for managers/admins always, and
 *     for staff when the manager shared it OR assigned it to them — the server
 *     (GET /api/calls/:id/coaching) already scopes staff visibility, so any
 *     returned flag is showable.
 *
 * CP3 lifecycle: the flag carries a status chip (needs_review → … → closed),
 * and the ASSIGNED rep gets the one transition that's theirs — an
 * "Acknowledge" button (assigned → acknowledged via PATCH /api/calls/:id/flag),
 * which only ever appears post-reveal because the coaching payload itself is
 * gated behind the submitted self-score.
 *
 * Data comes from api.getCoaching. That endpoint is gated server-side exactly
 * like the AI reveal — a gated staff viewer gets an empty payload — so we treat
 * any error (or a payload with neither flag nor override) as "nothing to show"
 * and render null, keeping the screen quiet. Once the rep self-scores, this
 * panel lights up automatically (the self-score submit invalidates coaching).
 */
function flagTypeLabel(type: FlagType): string {
  return type === "exemplary" ? "Exemplary" : "Needs work";
}

function CoachingDisplay({ call }: { call: CallDetailModel }) {
  const role = useEffectiveRole();
  const isManager = isManagerRole(role);
  const { user } = useAuth();
  const { simulatedUser } = useSimulation();
  // Same effective-identity rule as the self-score owner check: the simulated
  // user while an admin is "viewing as" a rep, else the signed-in user.
  const effectiveUserId = simulatedUser?.id ?? user?.id ?? null;
  const queryClient = useQueryClient();

  const coachingQuery = useQuery({
    queryKey: queryKeys.calls.coaching(call.id),
    queryFn: ({ signal }) => api.getCoaching(call.id, signal),
    // A 403 (staff hitting the manager-gated route) is expected, not a bug —
    // don't retry it; just fall through to rendering nothing.
    retry: false,
  });

  // The rep's one lifecycle transition: assigned → acknowledged ("I saw this").
  const ackMutation = useMutation({
    mutationFn: () => api.transitionFlag(call.id, "acknowledged"),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.calls.coaching(call.id),
      });
    },
  });

  // Quiet while loading / on any error: this is a passive display, never a gate.
  const coaching: Coaching | undefined = coachingQuery.data;
  if (!coaching) return null;

  const override = coaching.override;
  const flag = coaching.flag;
  // The server scopes staff visibility (shared OR assigned-to-me), so any flag
  // it returns is showable; managers/admins always get the full flag.
  const showFlag = flag !== null;
  // The viewer is the assigned rep (acting as staff) — the acknowledge action
  // and the personal status copy are theirs.
  const isAssignee =
    !isManager &&
    flag?.assigned_to != null &&
    flag.assigned_to === effectiveUserId;
  const canAcknowledge = isAssignee && flag?.status === "assigned";

  // Nothing actionable to surface.
  if (!override && !showFlag) return null;

  return (
    <>
      {override && (
        <Card className="border-primary/40 bg-primary/5">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="text-sm font-semibold">Manager-adjusted score</h2>
              {override.manager_name && (
                <span className="text-xs text-muted-foreground">
                  by {override.manager_name}
                </span>
              )}
            </div>

            <div className="flex flex-wrap items-baseline gap-2">
              <span className={`text-2xl font-semibold tabular-nums ${scoreTextClass(override.total)}`}>
                {override.total}
                <span className="text-base font-normal text-muted-foreground">/100</span>
              </span>
              <Badge tone="primary">Final score</Badge>
            </div>

            <p className="text-sm whitespace-pre-wrap">{override.note}</p>

            {override.items.length > 0 && (
              <ul className="flex flex-col gap-2 border-t pt-3">
                {override.items.map((item) => (
                  <li key={item.criterion_key} className="flex flex-col gap-1">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">{item.label}</span>
                      <span className="text-sm font-semibold tabular-nums">
                        {item.points}
                        <span className="font-normal text-muted-foreground">/{item.weight}</span>
                      </span>
                    </div>
                    {item.note && (
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {item.note}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>
      )}

      {showFlag && flag && (
        <Card>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold">Coaching flag</h2>
              <Badge tone={flag.type === "exemplary" ? "success" : "warning"}>
                {flagTypeLabel(flag.type)}
              </Badge>
              {/* Lifecycle status: managers (and the assigned rep, for whom this
                  is their coaching item) see the full chip; other staff viewers
                  of a shared flag only see a quiet "Resolved" once closed. */}
              {isManager || isAssignee ? (
                <FlagStatusBadge status={flag.status} />
              ) : (
                flag.status === "closed" && <Badge tone="muted">Resolved</Badge>
              )}
            </div>
            {flag.assigned_to_name && (
              <p className="text-xs text-muted-foreground">
                Assigned to {flag.assigned_to_name}
                {flag.due_date ? ` · due ${flag.due_date}` : ""}
              </p>
            )}
            {flag.note && <p className="text-sm whitespace-pre-wrap">{flag.note}</p>}

            {/* The assigned rep's one action: acknowledge ("I saw this"). */}
            {canAcknowledge && (
              <div className="flex flex-col gap-1.5 border-t pt-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="primary"
                    onClick={() => ackMutation.mutate()}
                    disabled={ackMutation.isPending}
                  >
                    {ackMutation.isPending ? "Acknowledging…" : "Acknowledge"}
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    Lets your manager know you've seen this coaching item.
                  </span>
                </div>
                {ackMutation.isError && (
                  <p className="text-sm text-destructive">
                    {ackMutation.error instanceof ApiRequestError &&
                    ackMutation.error.body?.message
                      ? ackMutation.error.body.message
                      : "Couldn't acknowledge this item. Please try again."}
                  </p>
                )}
              </div>
            )}
            {isAssignee && flag.acknowledged_at && (
              <p className="text-xs text-emerald-600 dark:text-emerald-400">
                You acknowledged this on{" "}
                {new Date(flag.acknowledged_at).toLocaleString()}.
              </p>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

// ── Per-call processing cost (ADMIN-ONLY) ───────────────────────────────────
/**
 * What this single call cost to process — STT + AI, from GET
 * /api/admin/cost/calls/:id (v_cost_events: ai_audit_log runs ∪ stt_usage rows,
 * cents). The route is requireAdmin on the server; the CALLER (CallDetailLoaded)
 * additionally renders this ONLY for an effective-admin viewer, so non-admins
 * get total absence — never a locked placeholder (locked decision 2026-06-09).
 */
const COST_CATEGORY_LABELS: Record<string, string> = {
  stt_batch: "STT — batch transcription",
  stt_live: "STT — live stream",
  call_scoring: "AI — call scoring",
  call_classifier: "AI — call-type classifier",
  live_coaching: "AI — live coaching",
};

function costCategoryLabel(key: string): string {
  return COST_CATEGORY_LABELS[key] ?? key.replace(/_/g, " ");
}

/** True when a v_cost_events category is an STT row (vs an AI/Claude run). */
function isSttCategory(key: string): boolean {
  return key.startsWith("stt");
}

function AdminCostCard({ callId }: { callId: string }) {
  const costQuery = useQuery({
    queryKey: ["admin", "cost", "call", callId],
    queryFn: ({ signal }) => api.adminCostForCall(callId, signal),
  });

  // If the server disagrees about admin-ness (e.g. a stale session), stay
  // silent rather than surfacing an authorization error on a coaching screen.
  if (
    costQuery.error instanceof ApiRequestError &&
    (costQuery.error.status === 403 || costQuery.error.status === 404)
  ) {
    return null;
  }

  const data = costQuery.data;
  const categories = data ? Object.entries(data.by_category) : [];
  const sttCents = categories
    .filter(([k]) => isSttCategory(k))
    .reduce((sum, [, cents]) => sum + cents, 0);
  const aiCents = (data?.total_cents ?? 0) - sttCents;

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconInfo className="h-3.5 w-3.5" />}
        title="Processing cost"
        info="What this call cost to process (speech-to-text + AI). Visible to admins only."
        aside={
          data ? (
            <span className="text-sm font-semibold tabular-nums">
              {centsToDollars(data.total_cents)}
            </span>
          ) : undefined
        }
      />

      {costQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading cost…</p>
      )}

      {costQuery.isError && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn&apos;t load the cost for this call. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void costQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      )}

      {data && data.events.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No processing cost recorded for this call yet.
        </p>
      )}

      {data && data.events.length > 0 && (
        <div className="flex flex-col gap-2">
          {/* STT vs AI headline split. */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span className="text-muted-foreground">
              STT{" "}
              <span className="font-medium tabular-nums text-foreground">
                {centsToDollars(sttCents)}
              </span>
            </span>
            <span className="text-muted-foreground">
              AI{" "}
              <span className="font-medium tabular-nums text-foreground">
                {centsToDollars(aiCents)}
              </span>
            </span>
            <span className="ml-auto text-xs text-muted-foreground">
              {data.events.length} event{data.events.length === 1 ? "" : "s"}
            </span>
          </div>

          {/* Per-category rows, biggest first. */}
          <ul className="flex flex-col divide-y rounded-md border text-sm">
            {categories
              .sort((a, b) => b[1] - a[1])
              .map(([category, cents]) => (
                <li
                  key={category}
                  className="flex items-center justify-between gap-2 px-2.5 py-1.5"
                >
                  <span className="min-w-0 truncate text-muted-foreground">
                    {costCategoryLabel(category)}
                  </span>
                  <span className="shrink-0 font-medium tabular-nums">
                    {centsToDollars(cents)}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

// ── Manager actions (manager / admin only) ──────────────────────────────────
/**
 * The manager's control surface for a single call: flag it exemplary / needs
 * work (with an optional note + a "share with rep" toggle), assign the coaching
 * to a rep (with an optional due date + note), and override the AI score (a
 * per-criterion points form with a REQUIRED note; the total auto-sums).
 *
 * Each action is a mutation that invalidates the coaching query (so the
 * CoachingDisplay above refreshes) plus the call detail. The rubric criteria for
 * the override form are sourced from the AI reveal (criterion_key + label +
 * weight) — managers can always reveal AI — falling back to an existing
 * override's items, so the form works even before/without an AI score.
 */
function ManagerActions({ call }: { call: CallDetailModel }) {
  const role = useEffectiveRole();
  if (!isManagerRole(role)) return null;
  return <ManagerActionsBody call={call} />;
}

/** Which manager action form is currently open (one at a time). */
type ManagerPanel = "flag" | "assign" | "override" | null;

function ManagerActionsBody({ call }: { call: CallDetailModel }) {
  const queryClient = useQueryClient();

  // One form open at a time: opening a section collapses the others (Point 7).
  const [panel, setPanel] = useState<ManagerPanel>(null);
  const toggle = (p: Exclude<ManagerPanel, null>) =>
    setPanel((cur) => (cur === p ? null : p));

  // Refresh the coaching panel + the call detail after any manager action, then
  // collapse the form so the result shows in CoachingDisplay rather than as a
  // persistent open form.
  const invalidateCoaching = () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.calls.coaching(call.id),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.calls.detail(call.id),
    });
    setPanel(null);
  };

  return (
    <Card className="shrink-0">
      <SectionHeader
        icon={<IconCheckCircle className="h-3.5 w-3.5" />}
        title="Manager actions"
      />
      <div className="flex flex-col gap-2">
        <ManagerAccordionItem
          title="Flag"
          subtitle="Mark exemplary or needs-work"
          open={panel === "flag"}
          onToggle={() => toggle("flag")}
        >
          <FlagControl call={call} onSaved={invalidateCoaching} />
        </ManagerAccordionItem>

        <ManagerAccordionItem
          title="Assign"
          subtitle="Hand this call to a rep"
          open={panel === "assign"}
          onToggle={() => toggle("assign")}
        >
          <AssignControl call={call} onSaved={invalidateCoaching} />
        </ManagerAccordionItem>

        <ManagerAccordionItem
          title="Override"
          subtitle="Replace the AI score"
          open={panel === "override"}
          onToggle={() => toggle("override")}
        >
          <OverrideForm call={call} onSaved={invalidateCoaching} />
        </ManagerAccordionItem>
      </div>
    </Card>
  );
}

/** A compact collapsible row for one manager action. Collapsed = a single
 *  button line (title + subtitle + chevron); expanded reveals the form. */
function ManagerAccordionItem({
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-md border ${open ? "bg-muted/20" : ""}`}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-accent/40"
      >
        <span className="flex min-w-0 flex-col">
          <span className="text-sm font-medium">{title}</span>
          <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
        </span>
        <IconChevronDown
          className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="border-t px-3 py-3">{children}</div>}
    </div>
  );
}

// — Flag (exemplary / needs-work) —
function FlagControl({
  call,
  onSaved,
}: {
  call: CallDetailModel;
  onSaved: () => void;
}) {
  const [note, setNote] = useState("");
  const [shared, setShared] = useState(false);

  const flagMutation = useMutation({
    mutationFn: (type: FlagType) =>
      api.setFlag(call.id, {
        type,
        note: note.trim() || undefined,
        shared,
      }),
    onSuccess: () => {
      onSaved();
    },
  });

  // Which button is in-flight (so only the clicked one shows a pending label).
  const pendingType = flagMutation.isPending
    ? (flagMutation.variables as FlagType)
    : null;

  return (
    <div className="flex flex-col gap-3">
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note for this flag…"
        rows={2}
        disabled={flagMutation.isPending}
        className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
      />

      <label className="flex cursor-pointer items-center gap-2 text-sm select-none">
        <input
          type="checkbox"
          className="h-4 w-4 rounded border accent-primary"
          checked={shared}
          disabled={flagMutation.isPending}
          onChange={(e) => setShared(e.target.checked)}
        />
        <span>Share with the rep</span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          onClick={() => flagMutation.mutate("exemplary")}
          disabled={flagMutation.isPending}
        >
          {pendingType === "exemplary" ? "Saving…" : "Flag exemplary"}
        </Button>
        <Button
          variant="secondary"
          onClick={() => flagMutation.mutate("needs_work")}
          disabled={flagMutation.isPending}
        >
          {pendingType === "needs_work" ? "Saving…" : "Flag needs work"}
        </Button>
      </div>

      {flagMutation.isSuccess && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400">Flag saved.</p>
      )}
      {flagMutation.isError && (
        <p className="text-sm text-destructive">
          Couldn't save the flag. Please try again.
        </p>
      )}
    </div>
  );
}

// — Assign coaching to a rep —
function AssignControl({
  call,
  onSaved,
}: {
  call: CallDetailModel;
  onSaved: () => void;
}) {
  const [assignedTo, setAssignedTo] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");

  const staffQuery = useQuery({
    queryKey: queryKeys.manager.staff,
    queryFn: ({ signal }) => api.getManagerStaff(signal),
  });

  const assignMutation = useMutation({
    mutationFn: () =>
      api.assignCall(call.id, {
        assigned_to: assignedTo,
        due_date: dueDate || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      onSaved();
    },
  });

  const staff: StaffMember[] = staffQuery.data ?? [];
  const canSubmit = assignedTo !== "" && !assignMutation.isPending;

  return (
    <div className="flex flex-col gap-3">
      {staffQuery.isError ? (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-destructive">
            We couldn't load the rep list. Please try again.
          </p>
          <div>
            <Button variant="secondary" onClick={() => void staffQuery.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      ) : (
        <>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Rep</span>
            <select
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              disabled={staffQuery.isLoading || assignMutation.isPending}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            >
              <option value="">
                {staffQuery.isLoading ? "Loading reps…" : "Select a rep…"}
              </option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.extension_number ? ` · ext ${s.extension_number}` : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Due date (optional)</span>
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              disabled={assignMutation.isPending}
              className="rounded-md border bg-background px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
            />
          </label>

          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note for the rep…"
            rows={2}
            disabled={assignMutation.isPending}
            className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
          />

          <div>
            <Button
              variant="primary"
              onClick={() => assignMutation.mutate()}
              disabled={!canSubmit}
            >
              {assignMutation.isPending ? "Assigning…" : "Assign"}
            </Button>
          </div>

          {assignMutation.isSuccess && (
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              Coaching assigned.
            </p>
          )}
          {assignMutation.isError && (
            <p className="text-sm text-destructive">
              Couldn't assign this call. Please try again.
            </p>
          )}
        </>
      )}
    </div>
  );
}

// — Override the AI score —
/** A criterion the override form collects points for (seeded from AI reveal). */
interface OverrideCriterion {
  criterion_key: string;
  label: string;
  weight: number;
  initialPoints: number;
}

function OverrideForm({
  call,
  onSaved,
}: {
  call: CallDetailModel;
  onSaved: () => void;
}) {
  // Seed the criteria list. Managers can always reveal AI, so the AI reveal is
  // the authoritative source of {criterion_key, label, weight}; if an override
  // already exists, prefer its items' points as the starting values.
  const revealQuery = useQuery({
    queryKey: queryKeys.calls.aiReveal(call.id),
    queryFn: ({ signal }) => api.getAiReveal(call.id, signal),
    retry: false,
  });
  const coachingQuery = useQuery({
    queryKey: queryKeys.calls.coaching(call.id),
    queryFn: ({ signal }) => api.getCoaching(call.id, signal),
    retry: false,
  });

  // Build the criterion list once both sources have settled, preferring AI
  // reveal items; fall back to a prior override's items if AI isn't available.
  const criteria: OverrideCriterion[] = useMemo(() => {
    const reveal: AiReveal | undefined = revealQuery.data;
    const override = coachingQuery.data?.override ?? null;

    const priorPoints = new Map<string, number>(
      (override?.items ?? []).map((it) => [it.criterion_key, it.points]),
    );

    if (reveal && reveal.items.length > 0) {
      return reveal.items.map((it) => ({
        criterion_key: it.criterion_key,
        label: it.label,
        weight: it.weight,
        initialPoints: priorPoints.get(it.criterion_key) ?? it.points,
      }));
    }

    // No AI reveal — fall back to the existing override's items.
    if (override && override.items.length > 0) {
      return override.items.map((it) => ({
        criterion_key: it.criterion_key,
        label: it.label,
        weight: it.weight,
        initialPoints: it.points,
      }));
    }

    return [];
  }, [revealQuery.data, coachingQuery.data]);

  // Local editable points keyed by criterion. Seeded from `criteria` once loaded.
  const [points, setPoints] = useState<Record<string, number>>({});
  const [seeded, setSeeded] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (seeded || criteria.length === 0) return;
    const next: Record<string, number> = {};
    for (const c of criteria) next[c.criterion_key] = c.initialPoints;
    setPoints(next);
    setSeeded(true);
  }, [criteria, seeded]);

  const total = useMemo(
    () => Object.values(points).reduce((sum, p) => sum + (p || 0), 0),
    [points],
  );

  const overrideMutation = useMutation({
    mutationFn: () =>
      api.overrideScore(call.id, {
        total,
        note: note.trim(),
        items: criteria.map((c) => ({
          criterion_key: c.criterion_key,
          points: points[c.criterion_key] ?? 0,
        })),
      }),
    onSuccess: () => {
      onSaved();
    },
  });

  const isLoading = revealQuery.isLoading || coachingQuery.isLoading;
  const noteTrimmed = note.trim();
  const canSubmit =
    criteria.length > 0 &&
    noteTrimmed.length > 0 &&
    total >= 0 &&
    total <= 100 &&
    !overrideMutation.isPending;

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading rubric…</p>;
  }

  if (criteria.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No rubric is available for this call yet, so it can't be overridden.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <ul className="flex flex-col gap-2">
        {criteria.map((c) => (
          <li
            key={c.criterion_key}
            className="flex flex-wrap items-center justify-between gap-2"
          >
            <span className="text-sm font-medium">{c.label}</span>
            <label className="flex items-center gap-1.5 text-sm">
              <input
                type="number"
                min={0}
                max={c.weight}
                value={points[c.criterion_key] ?? 0}
                disabled={overrideMutation.isPending}
                onChange={(e) => {
                  const raw = Number(e.target.value);
                  const clamped = Number.isFinite(raw)
                    ? Math.max(0, Math.min(c.weight, Math.round(raw)))
                    : 0;
                  setPoints((prev) => ({
                    ...prev,
                    [c.criterion_key]: clamped,
                  }));
                }}
                className="w-20 rounded-md border bg-background px-2 py-1 text-sm tabular-nums outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
              />
              <span className="text-muted-foreground">/ {c.weight}</span>
            </label>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between border-t pt-2">
        <span className="text-sm font-medium">Total</span>
        <span className="text-lg font-semibold tabular-nums">
          {total}
          <span className="text-sm font-normal text-muted-foreground">/100</span>
        </span>
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="font-medium">
          Note <span className="text-destructive">*</span>
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Why are you overriding the score? (required, shown to the rep)"
          rows={3}
          disabled={overrideMutation.isPending}
          className="w-full resize-y rounded-md border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
        />
      </label>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="primary"
          onClick={() => overrideMutation.mutate()}
          disabled={!canSubmit}
        >
          {overrideMutation.isPending ? "Saving…" : "Save override"}
        </Button>
        {noteTrimmed.length === 0 && (
          <span className="text-xs text-muted-foreground">
            A note is required to override.
          </span>
        )}
      </div>

      {overrideMutation.isSuccess && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400">
          Override saved.
        </p>
      )}
      {overrideMutation.isError && (
        <p className="text-sm text-destructive">
          Couldn't save the override. Please try again.
        </p>
      )}
    </div>
  );
}
